import {
  BookOpenText,
  Check,
  Cloud,
  CloudOff,
  Download,
  LayoutGrid,
  MessageSquareWarning,
  Mic,
  MoreHorizontal,
  Plus,
  RotateCcw,
  Search,
  Send,
  SpellCheck,
  SquarePen,
  Square,
  Trash2,
  X
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type TouchEvent,
  type WheelEvent
} from "react";
import {
  PcmChunkRecorder,
  resampleTo16Khz,
  rmsToPercent,
  type RecorderDiagnostics
} from "./lib/audio";
import {
  applyCorrectionDictionary,
  loadCorrectionDictionary,
  normalizeCorrectionPhrase,
  saveCorrectionDictionary,
  upsertCorrection,
  type CorrectionEntry
} from "./lib/corrections";
import { combineDialogueSegments, formatDialogueTranscript } from "./lib/dialogue";
import {
  createLocalDocument,
  deleteDocument,
  deleteDocuments,
  getDefaultDocumentTitle,
  listDocuments,
  saveDocument,
  type LocalDocument
} from "./lib/documents";
import {
  deleteDriveDocument,
  deleteDriveDocuments,
  isGoogleDriveConfigured,
  listDriveDocuments,
  preloadGoogleDriveSignIn,
  requestDriveAccess,
  saveDriveDocument
} from "./lib/googleDrive";
import type {
  AudioChunk,
  InferenceDevice,
  ModelConfig,
  TranscriptEntry,
  WorkerOutboundMessage,
  WorkerPhase
} from "./types";

const DEFAULT_MODEL_ID = import.meta.env.VITE_DEFAULT_MODEL_ID || "Xenova/whisper-tiny.en";
const DEFAULT_REVISION = "main";
const DEFAULT_MODEL_BASE_URL = import.meta.env.VITE_MODEL_BASE_URL || "";
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";
const DEFAULT_TITLE = "Document";
const LEGACY_DEFAULT_TITLES = new Set(["Doc", "Untitled", "CloudWrite", "WhisperWriter"]);
const STORAGE_TITLE_KEY = "speechdraft.documentTitle";
const STORAGE_TEXT_KEY = "speechdraft.documentText";
const PAGE_WIDTH_KEY = "speechdraft.pageWidth";
const TEXT_SIZE_KEY = "speechdraft.textSize";
const PARAGRAPH_BREAK_KEY = "speechdraft.paragraphBreak";
const PARAGRAPH_BREAK_DEFAULT_VERSION_KEY = "speechdraft.paragraphBreakDefaultVersion";
const PARAGRAPH_BREAK_DEFAULT_VERSION = "indent-v1";
const ISSUE_REPORTER_KEY = "speechdraft.issueReporterId";
const ISSUE_REPORT_MAX_LENGTH = 1200;
const ISSUE_REPORT_ENDPOINT =
  import.meta.env.VITE_ISSUE_REPORT_ENDPOINT ||
  (import.meta.env.DEV ? "https://speechdraft.pages.dev/api/issues" : "/api/issues");
const LEGACY_STORAGE_TITLE_KEYS = ["whisperwriter.documentTitle", "cloudwrite.documentTitle"];
const LEGACY_STORAGE_TEXT_KEYS = ["whisperwriter.documentText", "cloudwrite.documentText"];
const ACTIVE_DOCUMENT_KEY = "speechdraft.activeDocumentId";
const LEGACY_ACTIVE_DOCUMENT_KEYS = ["whisperwriter.activeDocumentId"];
const MIN_USABLE_RMS_THRESHOLD = 0.00003;
const QUIET_RMS_THRESHOLD = 0.006;
const MICROPHONE_INPUT_RMS_THRESHOLD = MIN_USABLE_RMS_THRESHOLD;
const SPEECH_RMS_THRESHOLD = 0.001;
const AUTO_FINISH_SILENCE_MS = 2800;
const NO_MICROPHONE_INPUT_TIMEOUT_MS = 4500;
const NO_MICROPHONE_INPUT_MESSAGE =
  "SpeechDraft could not read microphone audio from this browser. Try a real device or another browser.";
const NO_SPEECH_TIMEOUT_MS = 8000;
const RECORDING_SLICE_SECONDS = 0.5;
const PAUSE_BREAK_SECONDS = 0.85;
const PARAGRAPH_PAUSE_SECONDS = 1.75;
const PAUSE_RMS_THRESHOLD = 0.009;
const SEGMENT_MIN_SECONDS = 10;
const EARLY_PARAGRAPH_BOUNDARY_SECONDS = 3;
const SEGMENT_TARGET_SECONDS = 22;
const SEGMENT_MAX_SECONDS = 28;
const TRANSCRIPTION_TARGET_RMS = 0.045;
const TRANSCRIPTION_MAX_GAIN = 24;
const MAX_RECORDING_SECONDS = 5 * 60;
const COUNTDOWN_WARNING_SECONDS = 30;
const LOCAL_SAVE_DEBOUNCE_MS = 350;
const DRIVE_SYNC_IDLE_MS = 15_000;
const DRIVE_SYNC_MAX_MS = 120_000;
const DRIVE_SYNC_RETRY_BASE_MS = 30_000;
const DRIVE_SYNC_RETRY_MAX_MS = 5 * 60_000;
const VOICE_DEVICE: InferenceDevice = "wasm";
const DEFAULT_PAGE_WIDTH = 760;
const MIN_PAGE_WIDTH = 620;
const MAX_PAGE_WIDTH = 920;
const DEFAULT_TEXT_SIZE = 19.5;
const MIN_TEXT_SIZE = 16;
const MAX_TEXT_SIZE = 26;
const EDITOR_MIN_HEIGHT_OFFSET = 210;
const MOBILE_KEYBOARD_INSET_THRESHOLD = 220;
const EDITOR_CARET_SCROLL_MARGIN = 34;
const MOBILE_KEYBOARD_ACCESSORY_CLEARANCE = 72;
const STARTER_DOCUMENT_ID = "speechdraft-starter-guide";
const STARTER_DOCUMENT_TITLE = "How to Dictate with SpeechDraft";
const STARTER_DOCUMENT_TEXT = `Dictation works best when you stop treating it like typing with your mouth.

The useful unit is not the recording. It is the thought. Start speaking when a thought is ready. Pause when it is not. Silence is not part of the draft; it is part of thinking.

Before a longer session, preload your mind. A rough outline, a scene goal, or a few bullet points is enough. You do not need a perfect plan, but you do need somewhere for the next sentence to go.

Use the regular microphone for narration, notes, and ordinary prose. Speak naturally and let SpeechDraft create paragraphs from the finished pieces. Do not say "period" or "comma" unless you actually want those words in the document.

Use the quote button for dialogue. It is built for lines like "Where are you going?" asked Amanda, or "That is absolutely crazy," said John. The app will try to place the quote marks and dialogue tag for you.

If the text is wrong, fix the smallest thing that matters. Highlight a word or phrase, add a correction to the dictionary, and SpeechDraft will apply it next time. Dictation is fast because you keep moving, not because every sentence arrives perfect.

For long work, think in passes. First get the raw scene or idea down. Then revise. A spoken draft can be messy and still be valuable if it captures momentum, conflict, and the shape of the thought.

The goal is simple: talk when the story is ready, stop when it is not, and let the document keep up.`;

type AppendMode = "paragraph" | "force-paragraph" | "auto" | "join";
type VoiceMode = "dictation" | "dialogue";
type LibraryView = "documents" | "trash";
type StorageMode = "local" | "drive";
type DriveStatus = "unavailable" | "available" | "connecting" | "ready";
type SavePhase = "saving" | "saved" | "local" | "error";
type StringStateUpdate = string | ((previous: string) => string);
type ParagraphBreakStyle = "spaced" | "indented";
type LibraryDocumentStatus = "active" | "trashed";
type IssueSubmitPhase = "idle" | "sending" | "sent" | "error";

interface FinishDictationOptions {
  transcribe?: boolean;
}

interface TranscriptionSegment {
  samples: Float32Array;
  durationSeconds: number;
  rms: number;
  appendMode: AppendMode;
  pauseBeforeSeconds: number;
}

interface SegmentBoundary {
  endIndex: number;
  reason: "final" | "forced" | "natural";
  pauseSeconds: number;
}

interface PauseCandidate {
  endIndex: number;
  pauseSeconds: number;
  score: number;
}

interface SegmentMetadata {
  appendMode: AppendMode;
  pauseBeforeSeconds: number;
  recordingId: string;
  sequence: number;
  voiceMode: VoiceMode;
}

interface DialogueRecording {
  pendingIds: Set<string>;
  segments: Array<{
    appendMode: AppendMode;
    sequence: number;
    text: string;
  }>;
}

interface CorrectionPopoverState {
  source: string;
  replacement: string;
  selectionStart: number;
  selectionEnd: number;
  left: number;
  top: number;
  mode: "offer" | "edit";
}

interface SaveFilePickerOptions {
  suggestedName?: string;
  types?: Array<{
    description: string;
    accept: Record<string, string[]>;
  }>;
}

interface SaveFileHandle {
  createWritable(): Promise<{
    close(): Promise<void>;
    write(data: Blob): Promise<void>;
  }>;
}

type SaveFilePickerWindow = Window & {
  showSaveFilePicker?: (options: SaveFilePickerOptions) => Promise<SaveFileHandle>;
};

function readStoredValue(key: string, defaultValue: string, legacyKeys: string[] = []): string {
  try {
    const storedValue = window.localStorage?.getItem(key);
    if (storedValue !== null && storedValue !== undefined) {
      return storedValue;
    }

    for (const legacyKey of legacyKeys) {
      const legacyValue = window.localStorage?.getItem(legacyKey);
      if (legacyValue !== null && legacyValue !== undefined) {
        writeStoredValue(key, legacyValue);
        return legacyValue;
      }
    }

    return defaultValue;
  } catch {
    return defaultValue;
  }
}

function writeStoredValue(key: string, value: string): void {
  try {
    window.localStorage?.setItem(key, value);
  } catch {
    // Storage can be unavailable in strict browser contexts. The editor still works in memory.
  }
}

function getIssueReporterId(): string {
  const existingId = readStoredValue(ISSUE_REPORTER_KEY, "");
  if (existingId) {
    return existingId;
  }

  const nextId = window.crypto?.randomUUID?.() ?? `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  writeStoredValue(ISSUE_REPORTER_KEY, nextId);
  return nextId;
}

function clampPageWidth(value: number): number {
  if (!Number.isFinite(value)) {
    return DEFAULT_PAGE_WIDTH;
  }

  return Math.min(MAX_PAGE_WIDTH, Math.max(MIN_PAGE_WIDTH, Math.round(value)));
}

function readStoredPageWidth(): number {
  return clampPageWidth(Number(readStoredValue(PAGE_WIDTH_KEY, String(DEFAULT_PAGE_WIDTH))));
}

function clampTextSize(value: number): number {
  if (!Number.isFinite(value)) {
    return DEFAULT_TEXT_SIZE;
  }

  return Math.min(MAX_TEXT_SIZE, Math.max(MIN_TEXT_SIZE, Math.round(value * 2) / 2));
}

function readStoredTextSize(): number {
  return clampTextSize(Number(readStoredValue(TEXT_SIZE_KEY, String(DEFAULT_TEXT_SIZE))));
}

function readStoredParagraphBreakStyle(): ParagraphBreakStyle {
  const storedValue = readStoredValue(PARAGRAPH_BREAK_KEY, "");
  if (storedValue === "indented") {
    return "indented";
  }

  if (
    storedValue === "spaced" &&
    readStoredValue(PARAGRAPH_BREAK_DEFAULT_VERSION_KEY, "") === PARAGRAPH_BREAK_DEFAULT_VERSION
  ) {
    return "spaced";
  }

  return "indented";
}

function buildTranscriptionSegments(chunks: AudioChunk[]): TranscriptionSegment[] {
  const thresholds = getAdaptiveAudioThresholds(chunks);
  const segments: Array<{ chunks: AudioChunk[]; appendMode: AppendMode; pauseBeforeSeconds: number }> = [];
  let startIndex = findNextSpeechIndex(chunks, 0, thresholds.quiet);
  let appendMode: AppendMode = "paragraph";
  let pauseBeforeSeconds = 0;

  while (startIndex < chunks.length) {
    const boundary = chooseSegmentBoundary(chunks, startIndex, thresholds.pause);
    const trimmed = trimQuietEdges(chunks.slice(startIndex, boundary.endIndex), thresholds.quiet);
    if (containsSpeech(trimmed, thresholds.quiet)) {
      segments.push({
        chunks: trimmed,
        appendMode,
        pauseBeforeSeconds
      });
    }
    appendMode = appendModeAfterBoundary(boundary);
    pauseBeforeSeconds = boundary.reason === "natural" ? boundary.pauseSeconds : 0;
    startIndex = findNextSpeechIndex(chunks, Math.max(boundary.endIndex, startIndex + 1), thresholds.quiet);
  }

  return segments.map((segment) => {
    const rawSamples = concatenateSamples(segment.chunks);
    const rms = calculateRms(segment.chunks);
    const samples = normalizeSamples(rawSamples, rms);
    const durationSeconds = samples.length / 16_000;
    return {
      samples,
      durationSeconds,
      rms,
      appendMode: segment.appendMode,
      pauseBeforeSeconds: segment.pauseBeforeSeconds
    };
  });
}

function getAdaptiveAudioThresholds(chunks: AudioChunk[]): { quiet: number; pause: number } {
  const peakRms = chunks.reduce((peak, chunk) => Math.max(peak, chunk.rms), 0);
  const quiet = Math.min(
    QUIET_RMS_THRESHOLD,
    Math.max(MIN_USABLE_RMS_THRESHOLD, peakRms * 0.18)
  );
  const pause = Math.min(
    PAUSE_RMS_THRESHOLD,
    Math.max(MIN_USABLE_RMS_THRESHOLD, peakRms * 0.22)
  );

  return { quiet, pause };
}

function findNextSpeechIndex(chunks: AudioChunk[], fromIndex: number, quietThreshold: number): number {
  let index = fromIndex;

  while (index < chunks.length && (chunks[index]?.rms ?? 0) < quietThreshold) {
    index += 1;
  }

  return index;
}

function chooseSegmentBoundary(
  chunks: AudioChunk[],
  startIndex: number,
  pauseThreshold: number
): SegmentBoundary {
  const hardEndIndex = findDurationBoundary(chunks, startIndex, SEGMENT_MAX_SECONDS);

  if (hardEndIndex >= chunks.length) {
    return {
      endIndex: chunks.length,
      reason: "final",
      pauseSeconds: 0
    };
  }

  const pauseBoundary = findPauseBoundary(chunks, startIndex, hardEndIndex, pauseThreshold);
  if (pauseBoundary) {
    return pauseBoundary;
  }

  return {
    endIndex: findLowestEnergyBoundary(chunks, startIndex, hardEndIndex),
    reason: "forced",
    pauseSeconds: 0
  };
}

function appendModeAfterBoundary(boundary: SegmentBoundary): AppendMode {
  if (boundary.reason === "forced") {
    return "join";
  }

  if (boundary.reason === "natural" && boundary.pauseSeconds >= PARAGRAPH_PAUSE_SECONDS) {
    return "paragraph";
  }

  return "auto";
}

function findDurationBoundary(chunks: AudioChunk[], startIndex: number, maxSeconds: number): number {
  let durationSeconds = 0;

  for (let index = startIndex; index < chunks.length; index += 1) {
    durationSeconds += chunks[index]?.durationSeconds ?? 0;

    if (durationSeconds >= maxSeconds) {
      return index + 1;
    }
  }

  return chunks.length;
}

function findPauseBoundary(
  chunks: AudioChunk[],
  startIndex: number,
  hardEndIndex: number,
  pauseThreshold: number
): SegmentBoundary | null {
  let elapsedSeconds = 0;
  let quietSeconds = 0;
  let quietEnergy = 0;
  let bestCandidate: PauseCandidate | null = null;

  const considerPause = (endIndex: number, elapsedAtPauseEnd: number) => {
    if (quietSeconds < PAUSE_BREAK_SECONDS) {
      return;
    }

    const isEarlyParagraphPause =
      quietSeconds >= PARAGRAPH_PAUSE_SECONDS &&
      elapsedAtPauseEnd >= EARLY_PARAGRAPH_BOUNDARY_SECONDS;
    if (!isEarlyParagraphPause && elapsedAtPauseEnd < SEGMENT_MIN_SECONDS) {
      return;
    }

    const averageRms = quietEnergy / Math.max(quietSeconds, Number.EPSILON);
    const pauseDepth = Math.max(0, pauseThreshold - averageRms) / pauseThreshold;
    const targetPenalty = Math.abs(elapsedAtPauseEnd - SEGMENT_TARGET_SECONDS) * 0.04;
    const earlyPenalty = elapsedAtPauseEnd < SEGMENT_TARGET_SECONDS ? 0.2 : 0;
    const candidate: PauseCandidate = {
      endIndex,
      pauseSeconds: quietSeconds,
      score: quietSeconds * 4 + pauseDepth - targetPenalty - earlyPenalty
    };

    if (!bestCandidate || candidate.score > bestCandidate.score) {
      bestCandidate = candidate;
    }
  };

  for (let index = startIndex; index < hardEndIndex; index += 1) {
    const chunk = chunks[index];
    if (!chunk) {
      continue;
    }

    elapsedSeconds += chunk.durationSeconds;

    if (chunk.rms < pauseThreshold) {
      quietSeconds += chunk.durationSeconds;
      quietEnergy += chunk.rms * chunk.durationSeconds;
    } else {
      considerPause(index, elapsedSeconds - chunk.durationSeconds);
      quietSeconds = 0;
      quietEnergy = 0;
    }
  }

  if (quietSeconds > 0) {
    considerPause(hardEndIndex, elapsedSeconds);
  }

  const candidate = bestCandidate as PauseCandidate | null;

  return candidate
    ? {
        endIndex: candidate.endIndex,
        reason: "natural",
        pauseSeconds: candidate.pauseSeconds
      }
    : null;
}

function findLowestEnergyBoundary(
  chunks: AudioChunk[],
  startIndex: number,
  hardEndIndex: number
): number {
  let elapsedSeconds = 0;
  let bestBoundary = hardEndIndex;
  let bestScore = Number.POSITIVE_INFINITY;

  for (let index = startIndex + 1; index < hardEndIndex; index += 1) {
    elapsedSeconds += chunks[index - 1]?.durationSeconds ?? 0;

    if (elapsedSeconds < SEGMENT_MIN_SECONDS) {
      continue;
    }

    const before = chunks[index - 1]?.rms ?? 0;
    const after = chunks[index]?.rms ?? before;
    const energyScore = (before + after) / 2;
    const targetPenalty = Math.abs(elapsedSeconds - SEGMENT_TARGET_SECONDS) * 0.0002;
    const score = energyScore + targetPenalty;

    if (score < bestScore) {
      bestScore = score;
      bestBoundary = index;
    }
  }

  return Math.max(startIndex + 1, bestBoundary);
}

function trimQuietEdges(chunks: AudioChunk[], quietThreshold: number): AudioChunk[] {
  let start = 0;
  let end = chunks.length;

  while (start < end && (chunks[start]?.rms ?? 0) < quietThreshold) {
    start += 1;
  }

  while (end > start && (chunks[end - 1]?.rms ?? 0) < quietThreshold) {
    end -= 1;
  }

  return chunks.slice(start, end);
}

function containsSpeech(chunks: AudioChunk[], quietThreshold: number): boolean {
  return chunks.some((chunk) => chunk.rms >= quietThreshold);
}

function concatenateSamples(chunks: AudioChunk[]): Float32Array {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.samples.length, 0);
  const output = new Float32Array(totalLength);
  let offset = 0;

  for (const chunk of chunks) {
    output.set(chunk.samples, offset);
    offset += chunk.samples.length;
  }

  return output;
}

function calculateRms(chunks: AudioChunk[]): number {
  let weightedEnergy = 0;
  let totalSamples = 0;

  for (const chunk of chunks) {
    weightedEnergy += chunk.rms * chunk.rms * chunk.samples.length;
    totalSamples += chunk.samples.length;
  }

  return Math.sqrt(weightedEnergy / Math.max(1, totalSamples));
}

function normalizeSamples(samples: Float32Array, rms: number): Float32Array {
  if (rms < MIN_USABLE_RMS_THRESHOLD) {
    return samples;
  }

  const gain = Math.min(TRANSCRIPTION_MAX_GAIN, Math.max(1, TRANSCRIPTION_TARGET_RMS / rms));
  if (gain === 1) {
    return samples;
  }

  const normalized = new Float32Array(samples.length);
  for (let index = 0; index < samples.length; index += 1) {
    normalized[index] = Math.max(-1, Math.min(1, (samples[index] ?? 0) * gain));
  }

  return normalized;
}

function formatRecorderFailure(diagnostics?: RecorderDiagnostics): string {
  if (!diagnostics) {
    return "SpeechDraft opened the microphone, but no audio reached the recorder.";
  }

  const details = [
    `context ${diagnostics.audioContextState}`,
    `track ${diagnostics.trackReadyState}${diagnostics.trackMuted ? " muted" : ""}`,
    `frames ${diagnostics.processorTicks}`,
    `level ${diagnostics.levelPeakRms.toFixed(5)}`,
    `chunks ${diagnostics.pcmChunkCount}`,
    `backup ${Math.round(diagnostics.fallbackBytes / 1024)} KB`
  ];

  if (diagnostics.fallbackDecodeError) {
    details.push("backup decode failed");
  }

  if (diagnostics.fallbackStopTimedOut) {
    details.push("backup stop timed out");
  }

  return `SpeechDraft opened the microphone, but no usable audio reached Whisper. ${details.join(" · ")}.`;
}

function shouldShowRecorderFailure(diagnostics: RecorderDiagnostics | undefined, chunks: AudioChunk[]): boolean {
  if (!diagnostics) {
    return chunks.length === 0;
  }

  if (diagnostics.fallbackDecodeError || diagnostics.fallbackStopTimedOut) {
    return true;
  }

  const peakChunkRms = chunks.reduce((peak, chunk) => Math.max(peak, chunk.rms), 0);
  const peakRms = Math.max(diagnostics.levelPeakRms, diagnostics.pcmPeakRms, peakChunkRms);

  return diagnostics.fallbackBytes === 0 && diagnostics.fallbackChunks === 0 && peakRms < MIN_USABLE_RMS_THRESHOLD;
}

function startsWithLowercaseWord(text: string): boolean {
  const firstLetter = text.match(/[A-Za-z]/)?.[0];

  return Boolean(firstLetter && firstLetter === firstLetter.toLowerCase());
}

function endsWithSentencePunctuation(text: string): boolean {
  return /[.!?][)"'\]]*$/.test(text.trimEnd());
}

function shouldAppendInline(previous: string, next: string, appendMode: AppendMode): boolean {
  if (appendMode === "force-paragraph") {
    return false;
  }

  if (appendMode === "join" || appendMode === "auto") {
    return true;
  }

  return startsWithLowercaseWord(next) || !endsWithSentencePunctuation(previous);
}

function joinTranscriptText(previous: string, next: string): string {
  const previousText = startsWithLowercaseWord(next)
    ? previous.trimEnd().replace(/[.!?]+([)"'\]]*)$/, "$1")
    : previous.trimEnd();
  const separator = /^[,.;:!?]/.test(next) ? "" : " ";

  return `${previousText}${separator}${next}`;
}

function getParagraphSeparator(paragraphBreakStyle: ParagraphBreakStyle): string {
  return paragraphBreakStyle === "indented" ? "\n\t" : "\n\n";
}

function joinParagraphText(
  previous: string,
  next: string,
  paragraphBreakStyle: ParagraphBreakStyle
): string {
  const separator = getParagraphSeparator(paragraphBreakStyle);
  return `${previous.trimEnd()}${separator}${next}`;
}

function ensureTrailingParagraphBreak(previous: string, paragraphBreakStyle: ParagraphBreakStyle): string {
  if (!previous.trim()) {
    return previous;
  }

  const separator = getParagraphSeparator(paragraphBreakStyle);
  if (paragraphBreakStyle === "spaced" && /\n\s*\n\s*$/.test(previous)) {
    return previous;
  }

  if (paragraphBreakStyle === "indented" && /\n\t?\s*$/.test(previous)) {
    return previous;
  }

  return `${previous.trimEnd()}${separator}`;
}

function consumeSpokenParagraphCommand(
  text: string,
  metadata: SegmentMetadata | undefined
): { appendMode: AppendMode; insertBreakOnly: boolean; text: string } {
  const cleaned = text.trim();
  const hasPauseBefore = (metadata?.pauseBeforeSeconds ?? 0) >= PAUSE_BREAK_SECONDS;
  if (!hasPauseBefore || !/^new paragraph\b/i.test(cleaned)) {
    return {
      appendMode: metadata?.appendMode ?? "paragraph",
      insertBreakOnly: false,
      text: cleaned
    };
  }

  const textWithoutCommand = cleaned
    .replace(/^new paragraph\b[\s,.;:!?-]*/i, "")
    .trim();

  return {
    appendMode: "force-paragraph",
    insertBreakOnly: textWithoutCommand.length === 0,
    text: textWithoutCommand
  };
}

function formatCountdown(seconds: number): string {
  const clampedSeconds = Math.max(0, Math.ceil(seconds));
  const minutes = Math.floor(clampedSeconds / 60);
  const remainingSeconds = clampedSeconds % 60;

  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

function formatDocumentDate(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric"
  }).format(new Date(timestamp));
}

function isPlaceholderTitle(title: string): boolean {
  const normalizedTitle = title.trim();
  return normalizedTitle === DEFAULT_TITLE || LEGACY_DEFAULT_TITLES.has(normalizedTitle);
}

function formatExportTimestamp(timestamp = Date.now()): string {
  const date = new Date(timestamp);
  const pad = (value: number) => value.toString().padStart(2, "0");

  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
    `${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
  ].join("-");
}

function getDocumentDisplayTitle(document: Pick<LocalDocument, "title" | "createdAt">): string {
  const normalizedTitle = document.title.trim();
  return normalizedTitle && !isPlaceholderTitle(normalizedTitle)
    ? normalizedTitle
    : getDefaultDocumentTitle(document.createdAt);
}

function getDocumentPreview(text: string): string {
  return text.replace(/\s+/g, " ").trim().slice(0, 260);
}

function getDocumentStatus(document: Pick<LocalDocument, "status">): LibraryDocumentStatus {
  return document.status === "trashed" ? "trashed" : "active";
}

function hasDocumentChanges(
  previous: LocalDocument | undefined,
  next: LocalDocument
): boolean {
  if (!previous) {
    return true;
  }

  return (
    previous.title !== next.title ||
    previous.text !== next.text ||
    getDocumentStatus(previous) !== getDocumentStatus(next) ||
    previous.archivedAt !== next.archivedAt ||
    previous.trashedAt !== next.trashedAt
  );
}

function getLibraryTitle(libraryView: LibraryView): string {
  if (libraryView === "trash") {
    return "Trash folder";
  }

  return "Documents";
}

function getLibraryCountLabel(libraryView: LibraryView, count: number): string {
  if (libraryView === "trash") {
    return count === 1 ? "1 in trash" : `${count} in trash`;
  }

  return count === 1 ? "1 document" : `${count} documents`;
}

function getDriveConnectionErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  const normalizedMessage = message.toLowerCase();

  if (
    normalizedMessage.includes("popup") ||
    normalizedMessage.includes("policy") ||
    normalizedMessage.includes("comply") ||
    normalizedMessage.includes("origin") ||
    normalizedMessage.includes("disallowed") ||
    normalizedMessage.includes("cancel")
  ) {
    return "Google blocked Drive sign-in. Use http://localhost:5174 locally, or check OAuth test users and origins.";
  }

  return message;
}

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function safeFileName(value: string): string {
  return (value.trim() || "speechdraft").replace(/[^\w.-]+/g, "-");
}

function getEditorMinimumHeight(): number {
  return Math.max(window.innerHeight - EDITOR_MIN_HEIGHT_OFFSET, 360);
}

function createStarterDocument(): LocalDocument {
  return createLocalDocument({
    id: STARTER_DOCUMENT_ID,
    title: STARTER_DOCUMENT_TITLE,
    text: STARTER_DOCUMENT_TEXT
  });
}

function getDocumentExportFileName(document: Pick<LocalDocument, "title" | "createdAt">): string {
  return `${safeFileName(getDocumentDisplayTitle(document))}-${formatExportTimestamp()}.md`;
}

function formatMarkdownBundle(documentsToExport: LocalDocument[]): string {
  return documentsToExport
    .map((document) => {
      const title = getDocumentDisplayTitle(document).replace(/\s+/g, " ").trim();
      return `# ${title}\n\n${document.text.trim()}`;
    })
    .join("\n\n---\n\n")
    .trimEnd()
    .concat("\n");
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function triggerBrowserDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.rel = "noopener";
  link.style.display = "none";
  document.body.append(link);
  link.click();

  window.setTimeout(() => {
    URL.revokeObjectURL(url);
    link.remove();
  }, 1000);
}

async function saveMarkdownFile(fileName: string, content: string): Promise<"cancelled" | "saved"> {
  const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
  const saveFilePicker = (window as SaveFilePickerWindow).showSaveFilePicker?.bind(window);

  if (window.isSecureContext && saveFilePicker) {
    try {
      const fileHandle = await saveFilePicker({
        suggestedName: fileName,
        types: [
          {
            description: "Markdown document",
            accept: {
              "text/markdown": [".md"],
              "text/plain": [".txt"]
            }
          }
        ]
      });
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();
      return "saved";
    } catch (error) {
      if (isAbortError(error)) {
        return "cancelled";
      }
    }
  }

  triggerBrowserDownload(blob, fileName);
  return "saved";
}

function getTextareaSelectionPoint(
  textarea: HTMLTextAreaElement,
  position: number
): { left: number; top: number } | null {
  const ownerDocument = textarea.ownerDocument;
  const marker = ownerDocument.createElement("span");
  const mirror = ownerDocument.createElement("div");
  const textareaRect = textarea.getBoundingClientRect();
  const computedStyle = window.getComputedStyle(textarea);

  mirror.style.position = "fixed";
  mirror.style.visibility = "hidden";
  mirror.style.pointerEvents = "none";
  mirror.style.whiteSpace = "pre-wrap";
  mirror.style.overflowWrap = "break-word";
  mirror.style.boxSizing = computedStyle.boxSizing;
  mirror.style.top = `${textareaRect.top - textarea.scrollTop}px`;
  mirror.style.left = `${textareaRect.left - textarea.scrollLeft}px`;
  mirror.style.width = `${textarea.clientWidth}px`;
  mirror.style.minHeight = `${textarea.clientHeight}px`;
  mirror.style.border = computedStyle.border;
  mirror.style.padding = computedStyle.padding;
  mirror.style.font = computedStyle.font;
  mirror.style.letterSpacing = computedStyle.letterSpacing;
  mirror.style.lineHeight = computedStyle.lineHeight;
  mirror.style.tabSize = computedStyle.tabSize;

  mirror.textContent = textarea.value.slice(0, position);
  marker.textContent = "\u200b";
  mirror.append(marker);
  ownerDocument.body.append(mirror);

  const markerRect = marker.getBoundingClientRect();
  mirror.remove();

  if (markerRect.width === 0 && markerRect.height === 0) {
    return null;
  }

  return {
    left: markerRect.left,
    top: markerRect.top
  };
}

function getLayoutViewportHeight(): number {
  return Math.max(
    window.innerHeight,
    globalThis.document.documentElement.clientHeight || 0,
    globalThis.document.body?.clientHeight || 0,
    window.visualViewport?.height ?? 0
  );
}

function getVisualViewportHeight(): number {
  return Math.round(window.visualViewport?.height ?? window.innerHeight);
}

function getVisualViewportKeyboardInset(layoutViewportHeight = getLayoutViewportHeight()): number {
  const viewport = window.visualViewport;
  if (!viewport) {
    return 0;
  }

  const inset = layoutViewportHeight - viewport.height - viewport.offsetTop;
  const keyboardThreshold = Math.max(
    MOBILE_KEYBOARD_INSET_THRESHOLD,
    layoutViewportHeight * 0.28
  );
  return inset >= keyboardThreshold ? Math.round(inset) : 0;
}

function getIsMobileLayout(): boolean {
  return window.matchMedia?.("(max-width: 900px)").matches ?? window.innerWidth <= 900;
}

function getCorrectionPopoverPosition(textarea: HTMLTextAreaElement, position: number) {
  const selectionPoint = getTextareaSelectionPoint(textarea, position);
  const fallbackRect = textarea.getBoundingClientRect();
  const rawLeft = selectionPoint?.left ?? fallbackRect.left + fallbackRect.width / 2;
  const rawTop = selectionPoint?.top ?? fallbackRect.top + 88;
  const popoverWidth = 320;
  const left = Math.min(
    Math.max(16, rawLeft - popoverWidth / 2),
    Math.max(16, window.innerWidth - popoverWidth - 16)
  );
  const top = Math.max(82, rawTop - 58);

  return { left, top };
}

export function App() {
  const workerRef = useRef<Worker | null>(null);
  const recorderRef = useRef<PcmChunkRecorder | null>(null);
  const pendingIdsRef = useRef(new Set<string>());
  const segmentMetadataRef = useRef(new Map<string, SegmentMetadata>());
  const dialogueRecordingsRef = useRef(new Map<string, DialogueRecording>());
  const recordedChunksRef = useRef<AudioChunk[]>([]);
  const documentsRef = useRef<LocalDocument[]>([]);
  const currentDocumentIdRef = useRef("");
  const documentTitleRef = useRef("");
  const documentTextRef = useRef("");
  const storageModeRef = useRef<StorageMode>("local");
  const activeVoiceModeRef = useRef<VoiceMode>("dictation");
  const segmentSequenceRef = useRef(0);
  const finishingRef = useRef(false);
  const preparedRef = useRef(false);
  const documentsReadyRef = useRef(false);
  const documentRef = useRef<HTMLTextAreaElement | null>(null);
  const stageRef = useRef<HTMLElement | null>(null);
  const scrollToDocumentEndRef = useRef(false);
  const editorFocusedRef = useRef(false);
  const keyboardOpenRef = useRef(false);
  const keyboardModeStartedAtRef = useRef(0);
  const keyboardInsetRef = useRef(0);
  const visualViewportHeightRef = useRef(getVisualViewportHeight());
  const layoutViewportHeightRef = useRef(0);
  const caretScrollAnimationRef = useRef<number | undefined>(undefined);
  const caretScrollTimerRefs = useRef<number[]>([]);
  const lastTextInputAtRef = useRef(0);
  const touchScrollRef = useRef<{ x: number; y: number; handled: boolean } | null>(null);
  const correctionPopoverRef = useRef<HTMLDivElement | null>(null);
  const correctionInputRef = useRef<HTMLInputElement | null>(null);
  const saveRequestRef = useRef(0);
  const driveDirtyIdsRef = useRef(new Set<string>());
  const driveDirtySinceRef = useRef(new Map<string, number>());
  const driveSyncTimerRef = useRef<number | undefined>(undefined);
  const driveSyncRetryDelayRef = useRef(DRIVE_SYNC_RETRY_BASE_MS);
  const driveSyncingRef = useRef(false);
  const flushCurrentDocumentRef = useRef((options: { syncDrive?: boolean } = {}) => {
    void options;
  });
  const micActiveRef = useRef(false);
  const microphoneInputHeardRef = useRef(false);
  const speechHeardRef = useRef(false);
  const silenceTimerRef = useRef<number | undefined>(undefined);
  const noMicrophoneInputTimerRef = useRef<number | undefined>(undefined);
  const noSpeechTimerRef = useRef<number | undefined>(undefined);
  const recordingLimitTimerRef = useRef<number | undefined>(undefined);

  const [documents, setDocuments] = useState<LocalDocument[]>([]);
  const [currentDocumentId, setCurrentDocumentId] = useState("");
  const [documentTitle, setDocumentTitle] = useState("");
  const [documentText, setDocumentText] = useState("");
  const [viewMode, setViewMode] = useState<"editor" | "library">("editor");
  const [libraryView, setLibraryView] = useState<LibraryView>("documents");
  const [searchTerm, setSearchTerm] = useState("");
  const [actionMenuDocumentId, setActionMenuDocumentId] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [dictionaryOpen, setDictionaryOpen] = useState(false);
  const [pageWidth, setPageWidth] = useState(readStoredPageWidth);
  const [textSize, setTextSize] = useState(readStoredTextSize);
  const [keyboardInset, setKeyboardInset] = useState(0);
  const [visualViewportHeight, setVisualViewportHeight] = useState(getVisualViewportHeight);
  const [editorFocused, setEditorFocused] = useState(false);
  const [mobileLayout, setMobileLayout] = useState(getIsMobileLayout);
  const [paragraphBreakStyle, setParagraphBreakStyle] = useState<ParagraphBreakStyle>(
    readStoredParagraphBreakStyle
  );
  const [corrections, setCorrections] = useState<CorrectionEntry[]>(() => loadCorrectionDictionary());
  const correctionsRef = useRef(corrections);
  const [newCorrectionSource, setNewCorrectionSource] = useState("");
  const [newCorrectionReplacement, setNewCorrectionReplacement] = useState("");
  const [correctionPopover, setCorrectionPopover] = useState<CorrectionPopoverState | null>(null);
  const [documentsReady, setDocumentsReady] = useState(false);
  const [libraryError, setLibraryError] = useState("");
  const [storageMode, setStorageMode] = useState<StorageMode>("local");
  const [driveStatus, setDriveStatus] = useState<DriveStatus>(
    isGoogleDriveConfigured(GOOGLE_CLIENT_ID) ? "available" : "unavailable"
  );
  const [savePhase, setSavePhase] = useState<SavePhase>("saved");
  const [micActive, setMicActive] = useState(false);
  const [activeVoiceMode, setActiveVoiceMode] = useState<VoiceMode>("dictation");
  const [micError, setMicError] = useState("");
  const [workerPhase, setWorkerPhase] = useState<WorkerPhase>("idle");
  const [workerError, setWorkerError] = useState("");
  const [level, setLevel] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [transcripts, setTranscripts] = useState<TranscriptEntry[]>([]);
  const [remainingRecordingSeconds, setRemainingRecordingSeconds] = useState<number | null>(null);
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [issueText, setIssueText] = useState("");
  const [issueSubmitPhase, setIssueSubmitPhase] = useState<IssueSubmitPhase>("idle");
  const [issueError, setIssueError] = useState("");

  const modelConfig = useMemo<ModelConfig>(
    () => ({
      modelId: DEFAULT_MODEL_ID,
      modelBaseUrl: DEFAULT_MODEL_BASE_URL,
      revision: DEFAULT_REVISION,
      device: VOICE_DEVICE
    }),
    []
  );

  const currentDocument = useMemo(
    () => documents.find((document) => document.id === currentDocumentId) ?? null,
    [currentDocumentId, documents]
  );

  const documentStats = useMemo(() => {
    return {
      words: countWords(documentText)
    };
  }, [documentText]);

  const openIssueReporter = useCallback(() => {
    setIssueError("");
    setIssueSubmitPhase("idle");
    setIssueModalOpen(true);
  }, []);

  const closeIssueReporter = useCallback(() => {
    if (issueSubmitPhase === "sending") {
      return;
    }

    setIssueModalOpen(false);
  }, [issueSubmitPhase]);

  const submitIssueReport = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const message = issueText.trim();
      if (!message || issueSubmitPhase === "sending") {
        return;
      }

      setIssueSubmitPhase("sending");
      setIssueError("");

      try {
        const response = await fetch(ISSUE_REPORT_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            anonymousId: getIssueReporterId(),
            message,
            context: {
              app: "SpeechDraft",
              currentDocumentId,
              driveStatus,
              pageUrl: window.location.href,
              storageMode,
              userAgent: window.navigator.userAgent,
              viewportHeight: window.innerHeight,
              viewportWidth: window.innerWidth,
              voiceState: micActive ? activeVoiceMode : workerPhase,
              wordCount: documentStats.words
            }
          })
        });

        const result = (await response.json().catch(() => null)) as
          | { error?: string }
          | null;
        if (!response.ok) {
          throw new Error(result?.error || "Issue report failed.");
        }

        setIssueText("");
        setIssueSubmitPhase("sent");
        window.setTimeout(() => {
          setIssueModalOpen(false);
          setIssueSubmitPhase("idle");
        }, 450);
      } catch (error) {
        setIssueSubmitPhase("error");
        setIssueError(error instanceof Error ? error.message : "Issue report failed.");
      }
    },
    [
      activeVoiceMode,
      currentDocumentId,
      documentStats.words,
      driveStatus,
      issueSubmitPhase,
      issueText,
      micActive,
      storageMode,
      workerPhase
    ]
  );

  const nonTrashDocuments = useMemo(
    () => documents.filter((document) => getDocumentStatus(document) !== "trashed"),
    [documents]
  );

  const trashedDocuments = useMemo(
    () => documents.filter((document) => getDocumentStatus(document) === "trashed"),
    [documents]
  );

  const libraryDocuments = useMemo(() => {
    if (libraryView === "trash") {
      return trashedDocuments;
    }

    return nonTrashDocuments;
  }, [libraryView, nonTrashDocuments, trashedDocuments]);

  const filteredDocuments = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return libraryDocuments;
    }

    return libraryDocuments.filter((document) => {
      const displayTitle = getDocumentDisplayTitle(document).toLowerCase();
      return (
        displayTitle.includes(query) ||
        document.title.toLowerCase().includes(query) ||
        document.text.toLowerCase().includes(query)
      );
    });
  }, [libraryDocuments, searchTerm]);

  const countdownSeconds =
    micActive &&
    remainingRecordingSeconds !== null &&
    remainingRecordingSeconds <= COUNTDOWN_WARNING_SECONDS
      ? remainingRecordingSeconds
      : null;

  const isDictationActive = micActive && activeVoiceMode === "dictation";
  const isDialogueActive = micActive && activeVoiceMode === "dialogue";
  const voiceModelReady = workerPhase === "ready" || workerPhase === "transcribing" || micActive;
  const voiceModelPreparing = workerPhase === "idle" || workerPhase === "loading";
  const voiceUnavailable = workerPhase === "error" || Boolean(workerError);
  const voiceControlsDisabled = !voiceModelReady;
  const voiceStatusMessage = voiceUnavailable
    ? "Voice dictation could not load. Check your connection and reload."
    : voiceModelPreparing
      ? "Voice dictation is preparing."
      : "";

  const voiceActionClass = [
    "voice-action",
    isDictationActive ? "active" : "",
    voiceControlsDisabled ? "unavailable" : "",
    micActive && !isDictationActive ? "subdued" : "",
    isDictationActive && countdownSeconds !== null ? "warning" : ""
  ]
    .filter(Boolean)
    .join(" ");

  const dialogueActionClass = [
    "voice-action",
    "dialogue-action",
    isDialogueActive ? "active" : "",
    voiceControlsDisabled ? "unavailable" : "",
    micActive && !isDialogueActive ? "subdued" : "",
    isDialogueActive && countdownSeconds !== null ? "warning" : ""
  ]
    .filter(Boolean)
    .join(" ");

  const titlePlaceholder = currentDocument
    ? getDefaultDocumentTitle(currentDocument.createdAt)
    : DEFAULT_TITLE;
  const titleInputSize = Math.max(8, Math.min(48, documentTitle.length || titlePlaceholder.length));
  const keyboardOpen = viewMode === "editor" && editorFocused && mobileLayout && keyboardInset > 0;
  const appStyle = useMemo(
    () =>
      ({
        "--paper-width": `${pageWidth}px`,
        "--editor-text-size": `${textSize}px`,
        "--keyboard-inset": `${keyboardInset}px`,
        "--visual-viewport-height": `${visualViewportHeight}px`
      }) as CSSProperties,
    [keyboardInset, pageWidth, textSize, visualViewportHeight]
  );
  const appClassName = [
    "speechdraft-app",
    keyboardOpen ? "keyboard-open" : ""
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    keyboardOpenRef.current = keyboardOpen;
    if (keyboardOpen) {
      keyboardModeStartedAtRef.current = Date.now();
    }

    globalThis.document.documentElement.classList.toggle("speechdraft-keyboard-open", keyboardOpen);
    globalThis.document.body.classList.toggle("speechdraft-keyboard-open", keyboardOpen);

    if (!keyboardOpen && mobileLayout) {
      window.requestAnimationFrame(() => {
        window.scrollTo({ left: 0, top: 0, behavior: "auto" });
      });
    }

    return () => {
      globalThis.document.documentElement.classList.remove("speechdraft-keyboard-open");
      globalThis.document.body.classList.remove("speechdraft-keyboard-open");
    };
  }, [keyboardOpen, mobileLayout]);

  useEffect(() => {
    if (!mobileLayout) {
      return;
    }

    const blurEditorAfterKeyboardScroll = () => {
      if (!keyboardOpenRef.current || !editorFocusedRef.current) {
        return;
      }

      const now = Date.now();
      if (
        now - keyboardModeStartedAtRef.current < 900 ||
        now - lastTextInputAtRef.current < 600
      ) {
        return;
      }

      const editor = documentRef.current;
      if (editor && globalThis.document.activeElement === editor) {
        editor.blur();
      }
    };

    window.addEventListener("scroll", blurEditorAfterKeyboardScroll, { passive: true });
    window.visualViewport?.addEventListener("scroll", blurEditorAfterKeyboardScroll);

    return () => {
      window.removeEventListener("scroll", blurEditorAfterKeyboardScroll);
      window.visualViewport?.removeEventListener("scroll", blurEditorAfterKeyboardScroll);
    };
  }, [mobileLayout]);

  useEffect(() => {
    const mediaQuery = window.matchMedia?.("(max-width: 900px)");
    const updateMobileLayout = () => setMobileLayout(getIsMobileLayout());

    updateMobileLayout();
    mediaQuery?.addEventListener("change", updateMobileLayout);
    window.addEventListener("resize", updateMobileLayout);

    return () => {
      mediaQuery?.removeEventListener("change", updateMobileLayout);
      window.removeEventListener("resize", updateMobileLayout);
    };
  }, []);

  const updateCurrentDocumentId = useCallback((documentId: string) => {
    currentDocumentIdRef.current = documentId;
    setCurrentDocumentId(documentId);
  }, []);

  const updateDocumentTitle = useCallback((title: string) => {
    documentTitleRef.current = title;
    setDocumentTitle(title);
  }, []);

  const updateDocumentText = useCallback((nextValue: StringStateUpdate) => {
    const nextText =
      typeof nextValue === "function" ? nextValue(documentTextRef.current) : nextValue;
    documentTextRef.current = nextText;
    setDocumentText(nextText);
  }, []);

  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  useEffect(() => {
    currentDocumentIdRef.current = currentDocumentId;
  }, [currentDocumentId]);

  useEffect(() => {
    documentTitleRef.current = documentTitle;
  }, [documentTitle]);

  useEffect(() => {
    documentTextRef.current = documentText;
  }, [documentText]);

  useEffect(() => {
    storageModeRef.current = storageMode;
  }, [storageMode]);

  useEffect(() => {
    void preloadGoogleDriveSignIn(GOOGLE_CLIENT_ID).catch(() => {
      // Drive is optional; connection attempts surface the user-facing error.
    });
  }, []);

  useEffect(() => {
    writeStoredValue(PAGE_WIDTH_KEY, String(pageWidth));
  }, [pageWidth]);

  useEffect(() => {
    writeStoredValue(TEXT_SIZE_KEY, String(textSize));
  }, [textSize]);

  useEffect(() => {
    writeStoredValue(PARAGRAPH_BREAK_KEY, paragraphBreakStyle);
    writeStoredValue(PARAGRAPH_BREAK_DEFAULT_VERSION_KEY, PARAGRAPH_BREAK_DEFAULT_VERSION);
  }, [paragraphBreakStyle]);

  useEffect(() => {
    correctionsRef.current = corrections;
    saveCorrectionDictionary(corrections);
  }, [corrections]);

  useEffect(() => {
    setActionMenuDocumentId("");
    setSettingsOpen(false);
    setCorrectionPopover(null);
  }, [libraryView, searchTerm, viewMode]);

  useEffect(() => {
    if (!actionMenuDocumentId) {
      return;
    }

    const closeMenu = () => setActionMenuDocumentId("");
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    window.addEventListener("click", closeMenu);
    window.addEventListener("keydown", closeOnEscape);

    return () => {
      window.removeEventListener("click", closeMenu);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [actionMenuDocumentId]);

  useEffect(() => {
    if (!settingsOpen) {
      return;
    }

    const closeMenu = () => setSettingsOpen(false);
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    window.addEventListener("click", closeMenu);
    window.addEventListener("keydown", closeOnEscape);

    return () => {
      window.removeEventListener("click", closeMenu);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [settingsOpen]);

  useEffect(() => {
    if (!dictionaryOpen) {
      return;
    }

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDictionaryOpen(false);
      }
    };

    window.addEventListener("keydown", closeOnEscape);

    return () => {
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [dictionaryOpen]);

  useEffect(() => {
    if (!correctionPopover) {
      return;
    }

    const closePopover = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      if (
        correctionPopoverRef.current?.contains(target) ||
        documentRef.current?.contains(target)
      ) {
        return;
      }

      setCorrectionPopover(null);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setCorrectionPopover(null);
      }
    };

    window.addEventListener("mousedown", closePopover);
    window.addEventListener("keydown", closeOnEscape);

    return () => {
      window.removeEventListener("mousedown", closePopover);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [correctionPopover]);

  useEffect(() => {
    if (correctionPopover?.mode !== "edit") {
      return;
    }

    window.requestAnimationFrame(() => {
      correctionInputRef.current?.focus();
      correctionInputRef.current?.select();
    });
  }, [correctionPopover?.mode]);

  useEffect(() => {
    let cancelled = false;

    const loadLocalDocuments = async () => {
      try {
        let localDocuments = await listDocuments();

        if (localDocuments.length === 0) {
          const legacyTitle = readStoredValue(STORAGE_TITLE_KEY, "", LEGACY_STORAGE_TITLE_KEYS);
          const legacyText = readStoredValue(STORAGE_TEXT_KEY, "", LEGACY_STORAGE_TEXT_KEYS);
          const migratedTitle = legacyTitle.trim();
          const hasLegacyContent =
            Boolean(migratedTitle && !isPlaceholderTitle(migratedTitle)) ||
            Boolean(legacyText.trim());
          const migratedDocument = hasLegacyContent
            ? createLocalDocument({
                title: migratedTitle && !isPlaceholderTitle(migratedTitle) ? legacyTitle : undefined,
                text: legacyText
              })
            : createStarterDocument();
          await saveDocument(migratedDocument);
          localDocuments = [migratedDocument];
        }

        const activeDocumentId = readStoredValue(ACTIVE_DOCUMENT_KEY, "", LEGACY_ACTIVE_DOCUMENT_KEYS);
        const editableDocuments = localDocuments.filter(
          (document) => getDocumentStatus(document) !== "trashed"
        );
        const selectedDocument =
          editableDocuments.find((document) => document.id === activeDocumentId) ??
          editableDocuments[0] ??
          localDocuments[0];

        if (!selectedDocument || cancelled) {
          return;
        }

        documentsRef.current = localDocuments;
        documentsReadyRef.current = true;
        setDocuments(localDocuments);
        updateCurrentDocumentId(selectedDocument.id);
        updateDocumentTitle(getDocumentDisplayTitle(selectedDocument));
        updateDocumentText(selectedDocument.text);
        setDocumentsReady(true);
        writeStoredValue(ACTIVE_DOCUMENT_KEY, selectedDocument.id);
      } catch (error) {
        if (!cancelled) {
          setLibraryError(error instanceof Error ? error.message : String(error));
        }
      }
    };

    void loadLocalDocuments();

    return () => {
      cancelled = true;
    };
  }, [updateCurrentDocumentId, updateDocumentText, updateDocumentTitle]);

  const postToWorker = useCallback(
    (message: Parameters<Worker["postMessage"]>[0], transfer?: Transferable[]) => {
      workerRef.current?.postMessage(message, transfer ?? []);
    },
    []
  );

  const prepareVoice = useCallback(() => {
    if (preparedRef.current) {
      return;
    }

    preparedRef.current = true;
    setWorkerError("");
    postToWorker({
      type: "load",
      config: modelConfig
    });
  }, [modelConfig, postToWorker]);

  const addCorrectionRule = useCallback((source: string, replacement: string) => {
    setCorrections((items) => upsertCorrection(items, source, replacement));
  }, []);

  const showCorrectionPopoverFromSelection = useCallback(() => {
    const editor = documentRef.current;
    if (!editor || viewMode !== "editor") {
      return;
    }

    const selectionStart = editor.selectionStart;
    const selectionEnd = editor.selectionEnd;
    if (selectionEnd <= selectionStart) {
      setCorrectionPopover(null);
      return;
    }

    const selectedText = editor.value.slice(selectionStart, selectionEnd);
    const source = normalizeCorrectionPhrase(selectedText);
    if (!source || source.length > 140) {
      setCorrectionPopover(null);
      return;
    }

    const leadingWhitespace = selectedText.match(/^\s*/)?.[0].length ?? 0;
    const trailingWhitespace = selectedText.match(/\s*$/)?.[0].length ?? 0;
    const adjustedStart = selectionStart + leadingWhitespace;
    const adjustedEnd = selectionEnd - trailingWhitespace;
    const position = getCorrectionPopoverPosition(editor, adjustedEnd);

    setCorrectionPopover({
      source,
      replacement: source,
      selectionStart: adjustedStart,
      selectionEnd: adjustedEnd,
      left: position.left,
      top: position.top,
      mode: "offer"
    });
  }, [viewMode]);

  const handleEditorSelection = useCallback(() => {
    window.requestAnimationFrame(showCorrectionPopoverFromSelection);
  }, [showCorrectionPopoverFromSelection]);

  const openCorrectionEditor = useCallback(() => {
    setCorrectionPopover((current) => (current ? { ...current, mode: "edit" } : current));
  }, []);

  const saveSelectionCorrection = useCallback(
    (event?: FormEvent) => {
      event?.preventDefault();
      if (!correctionPopover) {
        return;
      }

      const source = normalizeCorrectionPhrase(correctionPopover.source);
      const replacement = correctionPopover.replacement.trim();
      if (!source || !replacement) {
        return;
      }

      addCorrectionRule(source, replacement);
      updateDocumentText((previous) => {
        const selectedText = previous.slice(
          correctionPopover.selectionStart,
          correctionPopover.selectionEnd
        );
        if (normalizeCorrectionPhrase(selectedText) !== source) {
          return previous;
        }

        return `${previous.slice(0, correctionPopover.selectionStart)}${replacement}${previous.slice(
          correctionPopover.selectionEnd
        )}`;
      });
      setCorrectionPopover(null);

      window.requestAnimationFrame(() => {
        const editor = documentRef.current;
        if (!editor) {
          return;
        }

        const caretPosition = correctionPopover.selectionStart + replacement.length;
        editor.focus();
        editor.setSelectionRange(caretPosition, caretPosition);
      });
    },
    [addCorrectionRule, correctionPopover, updateDocumentText]
  );

  const addDictionaryCorrection = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      const source = normalizeCorrectionPhrase(newCorrectionSource);
      const replacement = newCorrectionReplacement.trim();
      if (!source || !replacement) {
        return;
      }

      addCorrectionRule(source, replacement);
      setNewCorrectionSource("");
      setNewCorrectionReplacement("");
    },
    [addCorrectionRule, newCorrectionReplacement, newCorrectionSource]
  );

  const updateDictionaryCorrection = useCallback(
    (id: string, changes: Partial<Pick<CorrectionEntry, "source" | "replacement">>) => {
      setCorrections((items) =>
        items.map((entry) =>
          entry.id === id
            ? {
                ...entry,
                ...changes,
                updatedAt: Date.now()
              }
            : entry
        )
      );
    },
    []
  );

  const deleteDictionaryCorrection = useCallback((id: string) => {
    setCorrections((items) => items.filter((entry) => entry.id !== id));
  }, []);

  const appendToDocument = useCallback(
    (text: string, appendMode: AppendMode = "paragraph", applyDictionary = true) => {
      const corrected = applyDictionary
        ? applyCorrectionDictionary(text, correctionsRef.current)
        : text;
      const cleaned = corrected.trim();
      if (!cleaned || cleaned === "[no speech detected]") {
        return;
      }

      updateDocumentText((previous) => {
        if (!previous.trim()) {
          return cleaned;
        }

        return shouldAppendInline(previous, cleaned, appendMode)
          ? joinTranscriptText(previous, cleaned)
          : joinParagraphText(previous, cleaned, paragraphBreakStyle);
      });
      window.setTimeout(() => {
        flushCurrentDocumentRef.current({ syncDrive: true });
      }, 0);
    },
    [paragraphBreakStyle, updateDocumentText]
  );

  const appendParagraphBreakToDocument = useCallback(() => {
    updateDocumentText((previous) => ensureTrailingParagraphBreak(previous, paragraphBreakStyle));
    window.setTimeout(() => {
      flushCurrentDocumentRef.current({ syncDrive: true });
    }, 0);
  }, [paragraphBreakStyle, updateDocumentText]);

  const saveDocumentLocally = useCallback(async (document: LocalDocument) => {
    const localDocument = createLocalDocument(document);
    await saveDocument(localDocument);
    return localDocument;
  }, []);

  const markDriveDirty = useCallback(
    (documentId: string) => {
      if (storageModeRef.current !== "drive") {
        return;
      }

      driveDirtyIdsRef.current.add(documentId);
      if (!driveDirtySinceRef.current.has(documentId)) {
        driveDirtySinceRef.current.set(documentId, Date.now());
      }

      if (documentId === currentDocumentId) {
        setSavePhase("local");
      }
    },
    [currentDocumentId]
  );

  const syncDriveDirtyDocuments = useCallback(
    async (documentIds?: string[]) => {
      if (storageModeRef.current !== "drive" || driveSyncingRef.current) {
        return;
      }

      const dirtyIds = documentIds?.length
        ? documentIds.filter((id) => driveDirtyIdsRef.current.has(id))
        : Array.from(driveDirtyIdsRef.current);

      if (dirtyIds.length === 0) {
        return;
      }

      if (driveSyncTimerRef.current !== undefined) {
        window.clearTimeout(driveSyncTimerRef.current);
        driveSyncTimerRef.current = undefined;
      }

      driveSyncingRef.current = true;
      setLibraryError("");
      let syncFailed = false;

      try {
        for (const documentId of dirtyIds) {
          const documentToSync = documentsRef.current.find((document) => document.id === documentId);
          if (!documentToSync) {
            driveDirtyIdsRef.current.delete(documentId);
            driveDirtySinceRef.current.delete(documentId);
            continue;
          }

          const savedDocument = await saveDriveDocument(documentToSync);
          const latestDocument = documentsRef.current.find((document) => document.id === documentId);
          const documentChangedDuringSync = hasDocumentChanges(latestDocument, documentToSync);
          const documentForState =
            latestDocument && documentChangedDuringSync
              ? createLocalDocument({
                  ...latestDocument,
                  driveFileId: savedDocument.driveFileId,
                  createdAt: savedDocument.createdAt,
                  updatedAt: Math.max(latestDocument.updatedAt, savedDocument.updatedAt)
                })
              : savedDocument;

          documentsRef.current = [
            documentForState,
            ...documentsRef.current.filter((document) => document.id !== documentId)
          ].sort((left, right) => right.updatedAt - left.updatedAt);
          setDocuments(documentsRef.current);
          void saveDocument(documentForState).catch(() => {
            // Drive succeeded; local cache refresh is best-effort.
          });

          if (!documentChangedDuringSync) {
            driveDirtyIdsRef.current.delete(documentId);
            driveDirtySinceRef.current.delete(documentId);
          }
        }

        if (!currentDocumentId || !driveDirtyIdsRef.current.has(currentDocumentId)) {
          setSavePhase("saved");
        }
        driveSyncRetryDelayRef.current = DRIVE_SYNC_RETRY_BASE_MS;
      } catch (error) {
        syncFailed = true;
        setSavePhase("error");
        setLibraryError(error instanceof Error ? error.message : String(error));
      } finally {
        driveSyncingRef.current = false;
        if (
          storageModeRef.current === "drive" &&
          driveDirtyIdsRef.current.size > 0 &&
          driveSyncTimerRef.current === undefined
        ) {
          const retryDelay = driveSyncRetryDelayRef.current + Math.random() * DRIVE_SYNC_RETRY_BASE_MS;
          const nextDelay = syncFailed ? retryDelay : DRIVE_SYNC_IDLE_MS;
          if (syncFailed) {
            driveSyncRetryDelayRef.current = Math.min(
              driveSyncRetryDelayRef.current * 2,
              DRIVE_SYNC_RETRY_MAX_MS
            );
          }

          driveSyncTimerRef.current = window.setTimeout(() => {
            driveSyncTimerRef.current = undefined;
            void syncDriveDirtyDocuments();
          }, nextDelay);
        }
      }
    },
    [currentDocumentId]
  );

  const scheduleDriveSync = useCallback(() => {
    if (storageModeRef.current !== "drive" || driveDirtyIdsRef.current.size === 0) {
      return;
    }

    if (driveSyncTimerRef.current !== undefined) {
      window.clearTimeout(driveSyncTimerRef.current);
      driveSyncTimerRef.current = undefined;
    }

    const now = Date.now();
    const oldestDirtyAt = Math.min(...Array.from(driveDirtySinceRef.current.values()));
    const maxDelay = Math.max(0, oldestDirtyAt + DRIVE_SYNC_MAX_MS - now);
    const delay = Math.min(DRIVE_SYNC_IDLE_MS, maxDelay);

    driveSyncTimerRef.current = window.setTimeout(() => {
      driveSyncTimerRef.current = undefined;
      void syncDriveDirtyDocuments();
    }, delay);
  }, [syncDriveDirtyDocuments]);

  const deleteDocumentFromStorage = useCallback(async (document: LocalDocument) => {
    if (storageModeRef.current === "drive") {
      await deleteDriveDocument(document);
    }

    await deleteDocument(document.id);
  }, []);

  const deleteDocumentsFromStorage = useCallback(async (documentsToDelete: LocalDocument[]) => {
    if (storageModeRef.current === "drive") {
      await deleteDriveDocuments(documentsToDelete);
    }

    await deleteDocuments(documentsToDelete.map((document) => document.id));
  }, []);

  const persistCurrentDocument = useCallback(async (options: { syncDrive?: boolean } = {}) => {
    const activeDocumentId = currentDocumentIdRef.current;

    if (!documentsReadyRef.current || !activeDocumentId) {
      return;
    }

    const saveRequestId = ++saveRequestRef.current;
    setLibraryError("");

    const existingDocument = documentsRef.current.find(
      (document) => document.id === activeDocumentId
    );
    const nextDocument = createLocalDocument({
      ...existingDocument,
      id: activeDocumentId,
      title: documentTitleRef.current,
      text: documentTextRef.current,
      createdAt: existingDocument?.createdAt,
      updatedAt: Date.now()
    });
    const shouldSave = hasDocumentChanges(existingDocument, nextDocument);

    if (shouldSave) {
      documentsRef.current = [
        nextDocument,
        ...documentsRef.current.filter((document) => document.id !== currentDocumentId)
      ].sort((left, right) => right.updatedAt - left.updatedAt);
      setDocuments(documentsRef.current);
    }

    try {
      if (shouldSave) {
        await saveDocumentLocally(nextDocument);
        setSavePhase(storageModeRef.current === "drive" ? "local" : "saved");
      }

      if (storageModeRef.current === "drive" && (shouldSave || !nextDocument.driveFileId)) {
        markDriveDirty(nextDocument.id);
      }

      if (storageModeRef.current === "drive" && options.syncDrive) {
        await syncDriveDirtyDocuments([nextDocument.id]);
      }

      if (saveRequestRef.current === saveRequestId) {
        setSavePhase(
          storageModeRef.current === "drive" && driveDirtyIdsRef.current.has(nextDocument.id)
            ? "local"
            : "saved"
        );
      }
    } catch (error) {
      if (saveRequestRef.current === saveRequestId) {
        setSavePhase("error");
      }
      setLibraryError(error instanceof Error ? error.message : String(error));
      throw error;
    }
  }, [
    markDriveDirty,
    saveDocumentLocally,
    syncDriveDirtyDocuments
  ]);

  useEffect(() => {
    flushCurrentDocumentRef.current = (options: { syncDrive?: boolean } = {}) => {
      void persistCurrentDocument(options);
    };
  }, [persistCurrentDocument]);

  const applyCurrentDocument = useCallback((document: LocalDocument) => {
    writeStoredValue(ACTIVE_DOCUMENT_KEY, document.id);
    updateCurrentDocumentId(document.id);
    updateDocumentTitle(getDocumentDisplayTitle(document));
    updateDocumentText(document.text);
  }, [updateCurrentDocumentId, updateDocumentText, updateDocumentTitle]);

  const ensureEditableCurrentDocument = useCallback(
    async (nextDocuments: LocalDocument[]) => {
      const currentDocumentStillEditable = nextDocuments.some(
        (document) =>
          document.id === currentDocumentId && getDocumentStatus(document) !== "trashed"
      );

      if (currentDocumentStillEditable) {
        return nextDocuments;
      }

      const fallbackDocument = nextDocuments.find(
        (document) => getDocumentStatus(document) !== "trashed"
      );

      if (fallbackDocument) {
        applyCurrentDocument(fallbackDocument);
        return nextDocuments;
      }

      const blankDocument = createLocalDocument();
      const savedBlankDocument = await saveDocumentLocally(blankDocument);
      if (storageModeRef.current === "drive") {
        markDriveDirty(savedBlankDocument.id);
        scheduleDriveSync();
      }
      const withBlankDocument = [savedBlankDocument, ...nextDocuments].sort(
        (left, right) => right.updatedAt - left.updatedAt
      );
      applyCurrentDocument(savedBlankDocument);
      return withBlankDocument;
    },
    [applyCurrentDocument, currentDocumentId, markDriveDirty, saveDocumentLocally, scheduleDriveSync]
  );

  useEffect(() => {
    if (!documentsReady || !currentDocumentId) {
      return;
    }

    const saveRequestId = ++saveRequestRef.current;
    setLibraryError("");

    const existingDocument = documentsRef.current.find(
      (document) => document.id === currentDocumentId
    );
    const nextDocument = createLocalDocument({
      ...existingDocument,
      id: currentDocumentId,
      title: documentTitle,
      text: documentText,
      createdAt: existingDocument?.createdAt,
      updatedAt: Date.now()
    });
    const shouldSave = hasDocumentChanges(existingDocument, nextDocument);

    if (!shouldSave && nextDocument.driveFileId) {
      return;
    }

    if (shouldSave) {
      documentsRef.current = documentsRef.current
        .map((document) => (document.id === currentDocumentId ? nextDocument : document))
        .sort((left, right) => right.updatedAt - left.updatedAt);
      setDocuments(documentsRef.current);
    }

    if (storageModeRef.current === "drive" && (shouldSave || !nextDocument.driveFileId)) {
      markDriveDirty(nextDocument.id);
      scheduleDriveSync();
    }

    const saveTimer = window.setTimeout(() => {
      void saveDocumentLocally(nextDocument)
        .then(() => {
          if (saveRequestRef.current !== saveRequestId) {
            return;
          }

          setSavePhase(storageModeRef.current === "drive" ? "local" : "saved");
        })
        .catch((error: unknown) => {
          if (saveRequestRef.current !== saveRequestId) {
            return;
          }

          setSavePhase("error");
          setLibraryError(error instanceof Error ? error.message : String(error));
        });
    }, LOCAL_SAVE_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(saveTimer);
    };
  }, [
    currentDocumentId,
    documentText,
    documentTitle,
    documentsReady,
    markDriveDirty,
    saveDocumentLocally,
    scheduleDriveSync
  ]);

  useEffect(() => {
    const flushPendingChanges = () => {
      void persistCurrentDocument({ syncDrive: true });
    };
    const flushWhenHidden = () => {
      if (globalThis.document.visibilityState === "hidden") {
        flushPendingChanges();
      }
    };

    globalThis.document.addEventListener("visibilitychange", flushWhenHidden);
    window.addEventListener("pagehide", flushPendingChanges);
    window.addEventListener("beforeunload", flushPendingChanges);

    return () => {
      globalThis.document.removeEventListener("visibilitychange", flushWhenHidden);
      window.removeEventListener("pagehide", flushPendingChanges);
      window.removeEventListener("beforeunload", flushPendingChanges);
      if (driveSyncTimerRef.current !== undefined) {
        window.clearTimeout(driveSyncTimerRef.current);
        driveSyncTimerRef.current = undefined;
      }
    };
  }, [persistCurrentDocument]);

  const resizeDocumentPage = useCallback(() => {
    const editor = documentRef.current;
    if (!editor) {
      return;
    }

    if (mobileLayout && editorFocusedRef.current) {
      editor.style.height = `${Math.max(
        editor.scrollHeight,
        editor.offsetHeight,
        getEditorMinimumHeight()
      )}px`;
      return;
    }

    editor.style.height = "auto";
    editor.style.height = `${Math.max(editor.scrollHeight, getEditorMinimumHeight())}px`;
  }, [mobileLayout]);

  const queueDocumentPageResize = useCallback(() => {
    window.requestAnimationFrame(() => {
      resizeDocumentPage();
      window.requestAnimationFrame(resizeDocumentPage);
    });
  }, [resizeDocumentPage]);

  const scrollDocumentToEnd = useCallback(() => {
    scrollToDocumentEndRef.current = true;
  }, []);

  const scrollWritingStageToEnd = useCallback((behavior: ScrollBehavior) => {
    const stage = stageRef.current;
    if (!stage) {
      return;
    }

    stage.scrollTo({
      top: Math.max(0, stage.scrollHeight - stage.clientHeight),
      behavior
    });
  }, []);

  const scrollFocusedEditorCaretIntoView = useCallback((behavior: ScrollBehavior) => {
    const editor = documentRef.current;
    const stage = stageRef.current;
    if (!editor || !stage || globalThis.document.activeElement !== editor) {
      return;
    }

    const selectionPoint = getTextareaSelectionPoint(editor, editor.selectionStart);
    const editorRect = editor.getBoundingClientRect();
    const lineHeight = Number.parseFloat(window.getComputedStyle(editor).lineHeight) || 32;
    const caretTop = selectionPoint?.top ?? editorRect.top;
    const caretBottom = caretTop + lineHeight;

    if (mobileLayout && editorFocusedRef.current) {
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop ?? 0;
      const viewportBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      const stageRect = stage.getBoundingClientRect();
      const visibleTop = Math.max(stageRect.top, viewportTop) + EDITOR_CARET_SCROLL_MARGIN;
      const visibleBottom =
        Math.min(stageRect.bottom, viewportBottom) -
        EDITOR_CARET_SCROLL_MARGIN -
        MOBILE_KEYBOARD_ACCESSORY_CLEARANCE;

      if (visibleBottom <= visibleTop) {
        return;
      }

      if (caretBottom > visibleBottom) {
        stage.scrollTo({
          top: stage.scrollTop + caretBottom - visibleBottom,
          behavior
        });
        return;
      }

      if (caretTop < visibleTop) {
        stage.scrollTo({
          top: Math.max(0, stage.scrollTop - (visibleTop - caretTop)),
          behavior
        });
      }
      return;
    }

    const stageRect = stage.getBoundingClientRect();
    const viewport = window.visualViewport;
    const viewportTop = viewport?.offsetTop ?? 0;
    const viewportBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    const layoutViewportHeight = layoutViewportHeightRef.current || getLayoutViewportHeight();
    const keyboardTop =
      keyboardInsetRef.current > 0
        ? layoutViewportHeight - keyboardInsetRef.current
        : viewportBottom;
    const visibleTop = Math.max(stageRect.top, viewportTop) + EDITOR_CARET_SCROLL_MARGIN;
    const visibleBottom =
      Math.min(stageRect.bottom, viewportBottom, keyboardTop) - EDITOR_CARET_SCROLL_MARGIN;

    if (visibleBottom <= visibleTop) {
      return;
    }

    if (caretBottom > visibleBottom) {
      stage.scrollTo({
        top: stage.scrollTop + caretBottom - visibleBottom,
        behavior
      });
      return;
    }

    if (caretTop < visibleTop) {
      stage.scrollTo({
        top: Math.max(0, stage.scrollTop - (visibleTop - caretTop)),
        behavior
      });
    }
  }, [mobileLayout]);

  const queueFocusedEditorCaretScroll = useCallback(
    (behavior: ScrollBehavior = "auto") => {
      if (caretScrollAnimationRef.current !== undefined) {
        window.cancelAnimationFrame(caretScrollAnimationRef.current);
        caretScrollAnimationRef.current = undefined;
      }
      caretScrollTimerRefs.current.forEach((timerId) => window.clearTimeout(timerId));
      caretScrollTimerRefs.current = [];

      const scrollCaret = (nextBehavior: ScrollBehavior) => {
        resizeDocumentPage();
        scrollFocusedEditorCaretIntoView(nextBehavior);
      };

      caretScrollAnimationRef.current = window.requestAnimationFrame(() => {
        caretScrollAnimationRef.current = undefined;
        scrollCaret(behavior);
        window.requestAnimationFrame(() => {
          scrollCaret("auto");
        });
      });

      caretScrollTimerRefs.current = [120, 320].map((delay) => {
        const timerId = window.setTimeout(() => {
          caretScrollTimerRefs.current = caretScrollTimerRefs.current.filter((id) => id !== timerId);
          scrollCaret("auto");
        }, delay);
        return timerId;
      });
    },
    [resizeDocumentPage, scrollFocusedEditorCaretIntoView]
  );

  useEffect(
    () => () => {
      if (caretScrollAnimationRef.current !== undefined) {
        window.cancelAnimationFrame(caretScrollAnimationRef.current);
        caretScrollAnimationRef.current = undefined;
      }
      caretScrollTimerRefs.current.forEach((timerId) => window.clearTimeout(timerId));
      caretScrollTimerRefs.current = [];
    },
    []
  );

  useLayoutEffect(() => {
    if (viewMode !== "editor") {
      return;
    }

    resizeDocumentPage();

    if (scrollToDocumentEndRef.current) {
      scrollToDocumentEndRef.current = false;
      const editor = documentRef.current;
      if (editor) {
        editor.setSelectionRange(editor.value.length, editor.value.length);
      }

      scrollWritingStageToEnd("auto");
      window.requestAnimationFrame(() => {
        resizeDocumentPage();
        scrollWritingStageToEnd("smooth");
        window.requestAnimationFrame(() => {
          resizeDocumentPage();
          scrollWritingStageToEnd("auto");
        });
      });
      return;
    }

    if (editorFocusedRef.current) {
      queueFocusedEditorCaretScroll();
    } else {
      window.requestAnimationFrame(resizeDocumentPage);
    }
  }, [
    currentDocumentId,
    documentText,
    pageWidth,
    queueFocusedEditorCaretScroll,
    resizeDocumentPage,
    scrollWritingStageToEnd,
    textSize,
    viewMode
  ]);

  useEffect(() => {
    window.addEventListener("resize", queueDocumentPageResize);

    return () => {
      window.removeEventListener("resize", queueDocumentPageResize);
    };
  }, [queueDocumentPageResize]);

  useEffect(() => {
    const updateKeyboardInset = () => {
      const currentLayoutHeight = getLayoutViewportHeight();
      if (!layoutViewportHeightRef.current || !editorFocusedRef.current) {
        layoutViewportHeightRef.current = currentLayoutHeight;
      }

      const nextVisualViewportHeight = getVisualViewportHeight();
      const nextInset = getVisualViewportKeyboardInset(layoutViewportHeightRef.current);
      if (nextInset === 0) {
        layoutViewportHeightRef.current = currentLayoutHeight;
      }

      const insetChanged = keyboardInsetRef.current !== nextInset;
      const viewportHeightChanged = visualViewportHeightRef.current !== nextVisualViewportHeight;

      keyboardInsetRef.current = nextInset;
      visualViewportHeightRef.current = nextVisualViewportHeight;
      setKeyboardInset((currentInset) => (currentInset === nextInset ? currentInset : nextInset));
      setVisualViewportHeight((currentHeight) =>
        currentHeight === nextVisualViewportHeight ? currentHeight : nextVisualViewportHeight
      );

      if (editorFocusedRef.current && (insetChanged || viewportHeightChanged)) {
        queueDocumentPageResize();
        queueFocusedEditorCaretScroll();
      }
    };
    const viewport = window.visualViewport;

    updateKeyboardInset();
    window.addEventListener("resize", updateKeyboardInset);
    viewport?.addEventListener("resize", updateKeyboardInset);
    viewport?.addEventListener("scroll", updateKeyboardInset);
    const keyboardPollTimer = mobileLayout
      ? window.setInterval(updateKeyboardInset, 250)
      : undefined;

    return () => {
      window.removeEventListener("resize", updateKeyboardInset);
      viewport?.removeEventListener("resize", updateKeyboardInset);
      viewport?.removeEventListener("scroll", updateKeyboardInset);
      if (keyboardPollTimer !== undefined) {
        window.clearInterval(keyboardPollTimer);
      }
    };
  }, [mobileLayout, queueDocumentPageResize, queueFocusedEditorCaretScroll]);

  useEffect(() => {
    const fonts = globalThis.document.fonts;
    if (!fonts) {
      return;
    }

    let cancelled = false;
    void fonts.ready.then(() => {
      if (!cancelled) {
        queueDocumentPageResize();
      }
    });

    return () => {
      cancelled = true;
    };
  }, [currentDocumentId, queueDocumentPageResize, textSize]);

  useEffect(() => {
    if (viewMode !== "editor" || !currentDocumentId) {
      return;
    }

    stageRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [currentDocumentId, viewMode]);

  const scrollWritingStage = useCallback((event: WheelEvent<HTMLTextAreaElement>) => {
    if (!stageRef.current || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
      return;
    }

    event.preventDefault();
    stageRef.current.scrollTop += event.deltaY;
  }, []);

  const startWritingStageTouchScroll = useCallback((event: TouchEvent<HTMLTextAreaElement>) => {
    if (event.touches.length !== 1) {
      touchScrollRef.current = null;
      return;
    }

    const touch = event.touches[0];
    if (!touch) {
      touchScrollRef.current = null;
      return;
    }

    touchScrollRef.current = { x: touch.clientX, y: touch.clientY, handled: false };
  }, []);

  const moveWritingStageTouchScroll = useCallback((event: TouchEvent<HTMLTextAreaElement>) => {
    const stage = stageRef.current;
    const state = touchScrollRef.current;
    if (!stage || !state || event.touches.length !== 1) {
      return;
    }

    const touch = event.touches[0];
    if (!touch) {
      return;
    }

    const deltaX = touch.clientX - state.x;
    const deltaY = touch.clientY - state.y;
    if (!state.handled && Math.abs(deltaY) < 4) {
      return;
    }
    if (Math.abs(deltaY) <= Math.abs(deltaX)) {
      return;
    }

    event.preventDefault();
    state.handled = true;
    stage.scrollTop -= deltaY;
    state.x = touch.clientX;
    state.y = touch.clientY;
  }, []);

  const endWritingStageTouchScroll = useCallback(() => {
    touchScrollRef.current = null;
  }, []);

  const markSettled = useCallback((id?: string): SegmentMetadata | undefined => {
    const metadata = id ? segmentMetadataRef.current.get(id) : undefined;

    if (id && pendingIdsRef.current.delete(id)) {
      segmentMetadataRef.current.delete(id);
      setPendingCount(pendingIdsRef.current.size);
    }

    return metadata;
  }, []);

  const settleDialogueSegment = useCallback(
    (id: string, metadata: SegmentMetadata, text: string) => {
      const recording = dialogueRecordingsRef.current.get(metadata.recordingId);
      if (!recording) {
        return;
      }

      recording.pendingIds.delete(id);

      const cleaned = applyCorrectionDictionary(text, correctionsRef.current).trim();
      if (cleaned && cleaned !== "[no speech detected]") {
        recording.segments.push({
          appendMode: metadata.appendMode,
          sequence: metadata.sequence,
          text: cleaned
        });
      }

      if (recording.pendingIds.size > 0) {
        return;
      }

      dialogueRecordingsRef.current.delete(metadata.recordingId);
      const formatted = formatDialogueTranscript(combineDialogueSegments(recording.segments));
      if (formatted) {
        appendToDocument(formatted, "paragraph", false);
        scrollDocumentToEnd();
      }
    },
    [appendToDocument, scrollDocumentToEnd]
  );

  useEffect(
    () => () => {
      if (silenceTimerRef.current !== undefined) {
        window.clearTimeout(silenceTimerRef.current);
      }

      if (noMicrophoneInputTimerRef.current !== undefined) {
        window.clearTimeout(noMicrophoneInputTimerRef.current);
      }

      if (noSpeechTimerRef.current !== undefined) {
        window.clearTimeout(noSpeechTimerRef.current);
      }

      if (recordingLimitTimerRef.current !== undefined) {
        window.clearInterval(recordingLimitTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const worker = new Worker(new URL("./whisper.worker.ts", import.meta.url), {
      type: "module"
    });
    workerRef.current = worker;

    worker.onmessage = (event: MessageEvent<WorkerOutboundMessage>) => {
      const message = event.data;

      if (message.type === "model-loading") {
        setWorkerPhase("loading");
        setWorkerError("");
      }

      if (message.type === "model-ready") {
        setWorkerPhase("ready");
      }

      if (message.type === "transcription-start") {
        setWorkerPhase("transcribing");
        setMicError("");
      }

      if (message.type === "transcription-result") {
        const metadata = markSettled(message.id);
        const correctedText = applyCorrectionDictionary(message.text || "", correctionsRef.current);
        setWorkerPhase("ready");
        setMicError("");

        if (metadata?.voiceMode === "dialogue") {
          settleDialogueSegment(message.id, metadata, message.text);
        } else {
          const dictationCommand = consumeSpokenParagraphCommand(correctedText, metadata);
          if (dictationCommand.insertBreakOnly) {
            appendParagraphBreakToDocument();
          } else {
            appendToDocument(dictationCommand.text, dictationCommand.appendMode, false);
          }
          scrollDocumentToEnd();
        }

        setTranscripts((items) => [
          {
            id: message.id,
            sequence: message.sequence,
            text: correctedText || "[no speech detected]",
            durationSeconds: message.durationSeconds,
            inferenceSeconds: message.inferenceSeconds,
            rms: message.rms,
            createdAt: Date.now()
          },
          ...items.slice(0, 5)
        ]);
      }

      if (message.type === "worker-error") {
        const metadata = markSettled(message.id);
        if (message.id && metadata?.voiceMode === "dialogue") {
          settleDialogueSegment(message.id, metadata, "");
        }
        preparedRef.current = false;
        setWorkerPhase("error");
        setWorkerError(message.message);
      }
    };

    const idleCallbackId = window.requestIdleCallback?.(() => prepareVoice());
    const prepareFallbackTimerId = globalThis.setTimeout(prepareVoice, 1200);

    return () => {
      if (idleCallbackId !== undefined) {
        window.cancelIdleCallback?.(idleCallbackId);
      }
      globalThis.clearTimeout(prepareFallbackTimerId);
      worker.terminate();
      workerRef.current = null;
    };
  }, [
    appendParagraphBreakToDocument,
    appendToDocument,
    markSettled,
    prepareVoice,
    scrollDocumentToEnd,
    settleDialogueSegment
  ]);

  const recordChunk = useCallback((chunk: AudioChunk) => {
    if (!micActiveRef.current && !finishingRef.current) {
      return;
    }

    const samples = resampleTo16Khz(chunk.samples, chunk.sampleRate);
    recordedChunksRef.current.push({
      ...chunk,
      sampleRate: 16_000,
      samples,
      durationSeconds: samples.length / 16_000
    });
  }, []);

  const transcribeRecording = useCallback(
    (chunks: AudioChunk[], voiceMode: VoiceMode): boolean => {
      const segments = buildTranscriptionSegments(chunks);
      if (segments.length === 0) {
        return false;
      }

      const recordingId =
        globalThis.crypto?.randomUUID?.() ??
        `${Date.now()}-recording-${Math.random().toString(16).slice(2)}`;
      const dialogueRecording: DialogueRecording | undefined =
        voiceMode === "dialogue" && segments.length > 0
          ? {
              pendingIds: new Set<string>(),
              segments: []
            }
          : undefined;

      if (dialogueRecording) {
        dialogueRecordingsRef.current.set(recordingId, dialogueRecording);
      }

      for (const segment of segments) {
        const id =
          globalThis.crypto?.randomUUID?.() ??
          `${Date.now()}-${segmentSequenceRef.current}-${Math.random().toString(16).slice(2)}`;
        const sequence = ++segmentSequenceRef.current;

        pendingIdsRef.current.add(id);
        segmentMetadataRef.current.set(id, {
          appendMode: segment.appendMode,
          pauseBeforeSeconds: segment.pauseBeforeSeconds,
          recordingId,
          sequence,
          voiceMode
        });
        dialogueRecording?.pendingIds.add(id);
        setPendingCount(pendingIdsRef.current.size);

        postToWorker(
          {
            type: "transcribe",
            id,
            sequence,
            audio: segment.samples,
            durationSeconds: segment.durationSeconds,
            rms: segment.rms,
            config: modelConfig
          },
          [segment.samples.buffer]
        );
      }

      return true;
    },
    [modelConfig, postToWorker]
  );

  const clearVoiceTimers = useCallback(() => {
    if (silenceTimerRef.current !== undefined) {
      window.clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = undefined;
    }

    if (noMicrophoneInputTimerRef.current !== undefined) {
      window.clearTimeout(noMicrophoneInputTimerRef.current);
      noMicrophoneInputTimerRef.current = undefined;
    }

    if (noSpeechTimerRef.current !== undefined) {
      window.clearTimeout(noSpeechTimerRef.current);
      noSpeechTimerRef.current = undefined;
    }

    if (recordingLimitTimerRef.current !== undefined) {
      window.clearInterval(recordingLimitTimerRef.current);
      recordingLimitTimerRef.current = undefined;
    }

    setRemainingRecordingSeconds(null);
  }, []);

  const finishDictation = useCallback(async (options: FinishDictationOptions = {}) => {
    if (finishingRef.current) {
      return;
    }

    finishingRef.current = true;
    clearVoiceTimers();
    microphoneInputHeardRef.current = false;
    speechHeardRef.current = false;

    const recorder = recorderRef.current;
    const diagnostics = await recorder?.stop();
    recorderRef.current = null;
    micActiveRef.current = false;
    setMicActive(false);
    setLevel(0);

    const chunks = recordedChunksRef.current;
    recordedChunksRef.current = [];
    finishingRef.current = false;
    const voiceMode = activeVoiceModeRef.current;
    activeVoiceModeRef.current = "dictation";
    setActiveVoiceMode("dictation");
    if (options.transcribe !== false) {
      const queued = transcribeRecording(chunks, voiceMode);
      if (!queued && shouldShowRecorderFailure(diagnostics, chunks)) {
        setMicError(formatRecorderFailure(diagnostics));
      } else if (!queued) {
        setMicError("");
      }
    }
  }, [clearVoiceTimers, transcribeRecording]);

  const handleVoiceLevel = useCallback(
    (rms: number) => {
      const normalizedRms = Number.isFinite(rms) ? rms : 0;
      setLevel(rmsToPercent(normalizedRms));

      if (!micActiveRef.current) {
        return;
      }

      if (normalizedRms >= MICROPHONE_INPUT_RMS_THRESHOLD) {
        microphoneInputHeardRef.current = true;

        if (noMicrophoneInputTimerRef.current !== undefined) {
          window.clearTimeout(noMicrophoneInputTimerRef.current);
          noMicrophoneInputTimerRef.current = undefined;
        }
      }

      if (normalizedRms >= SPEECH_RMS_THRESHOLD) {
        speechHeardRef.current = true;

        if (silenceTimerRef.current !== undefined) {
          window.clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = undefined;
        }

        if (noSpeechTimerRef.current !== undefined) {
          window.clearTimeout(noSpeechTimerRef.current);
          noSpeechTimerRef.current = undefined;
        }

        return;
      }

      if (speechHeardRef.current && silenceTimerRef.current === undefined) {
        silenceTimerRef.current = window.setTimeout(() => {
          void finishDictation();
        }, AUTO_FINISH_SILENCE_MS);
      }
    },
    [finishDictation]
  );

  const startMic = useCallback(async (voiceMode: VoiceMode = "dictation") => {
    if (micActiveRef.current) {
      return;
    }
    if (!voiceModelReady) {
      prepareVoice();
      return;
    }

    setMicError("");
    setWorkerError("");
    activeVoiceModeRef.current = voiceMode;
    setActiveVoiceMode(voiceMode);
    prepareVoice();
    clearVoiceTimers();
    microphoneInputHeardRef.current = false;
    speechHeardRef.current = false;
    recordedChunksRef.current = [];
    finishingRef.current = false;

    const recorder = new PcmChunkRecorder();
    recorderRef.current = recorder;

    try {
      await recorder.start({
        chunkSeconds: RECORDING_SLICE_SECONDS,
        onChunk: recordChunk,
        onLevel: handleVoiceLevel
      });
      micActiveRef.current = true;
      setMicActive(true);
      setRemainingRecordingSeconds(MAX_RECORDING_SECONDS);
      const startedAt = performance.now();
      recordingLimitTimerRef.current = window.setInterval(() => {
        const elapsedSeconds = (performance.now() - startedAt) / 1000;
        const remainingSeconds = Math.max(0, MAX_RECORDING_SECONDS - elapsedSeconds);
        setRemainingRecordingSeconds(Math.ceil(remainingSeconds));

        if (remainingSeconds <= 0) {
          void finishDictation();
        }
      }, 250);
      noMicrophoneInputTimerRef.current = window.setTimeout(() => {
        if (!micActiveRef.current || microphoneInputHeardRef.current || speechHeardRef.current) {
          return;
        }

        if (recorder.hasEncodedFallback()) {
          return;
        }

        setMicError(NO_MICROPHONE_INPUT_MESSAGE);
        void finishDictation({ transcribe: false });
      }, NO_MICROPHONE_INPUT_TIMEOUT_MS);
      noSpeechTimerRef.current = window.setTimeout(() => {
        if (microphoneInputHeardRef.current && !speechHeardRef.current) {
          void finishDictation();
        }
      }, NO_SPEECH_TIMEOUT_MS);
    } catch (error) {
      recorderRef.current = null;
      micActiveRef.current = false;
      activeVoiceModeRef.current = "dictation";
      setActiveVoiceMode("dictation");
      clearVoiceTimers();
      setMicError(error instanceof Error ? error.message : String(error));
    }
  }, [clearVoiceTimers, finishDictation, handleVoiceLevel, prepareVoice, recordChunk, voiceModelReady]);

  const startDictation = useCallback(() => {
    void startMic("dictation");
  }, [startMic]);

  const startDialogue = useCallback(() => {
    void startMic("dialogue");
  }, [startMic]);

  const stopMic = useCallback(async () => {
    await finishDictation();
  }, [finishDictation]);

  const openLibrary = useCallback(() => {
    if (micActiveRef.current) {
      void finishDictation();
    }

    void persistCurrentDocument({ syncDrive: true });
    setLibraryView("documents");
    setSearchTerm("");
    setViewMode("library");
  }, [finishDictation, persistCurrentDocument]);

  const openEditor = useCallback(async () => {
    const nextDocuments = await ensureEditableCurrentDocument(documentsRef.current);
    documentsRef.current = nextDocuments;
    setDocuments(nextDocuments);
    setViewMode("editor");
  }, [ensureEditableCurrentDocument]);

  const openDocument = useCallback(
    async (document: LocalDocument) => {
      if (micActiveRef.current) {
        await finishDictation();
      }

      await persistCurrentDocument({ syncDrive: true });
      applyCurrentDocument(document);
      setViewMode("editor");
    },
    [applyCurrentDocument, finishDictation, persistCurrentDocument]
  );

  const createNewDocument = useCallback(async () => {
    if (micActiveRef.current) {
      try {
        await finishDictation();
      } catch (error) {
        setMicError(error instanceof Error ? error.message : String(error));
      }
    }

    setLibraryError("");

    const existingDocument = documentsRef.current.find(
      (document) => document.id === currentDocumentId
    );
    const currentSnapshot = currentDocumentId
      ? createLocalDocument({
          ...existingDocument,
          id: currentDocumentId,
          title: documentTitle,
          text: documentText,
          createdAt: existingDocument?.createdAt,
          updatedAt: Date.now()
        })
      : null;
    const nextDocument = createLocalDocument();
    const nextDocuments = [
      nextDocument,
      ...(currentSnapshot ? [currentSnapshot] : []),
      ...documentsRef.current.filter(
        (document) => document.id !== nextDocument.id && document.id !== currentDocumentId
      )
    ].sort(
      (left, right) => right.updatedAt - left.updatedAt
    );

    documentsRef.current = nextDocuments;
    documentsReadyRef.current = true;
    setDocuments(nextDocuments);
    setDocumentsReady(true);
    updateCurrentDocumentId(nextDocument.id);
    updateDocumentTitle(nextDocument.title);
    updateDocumentText(nextDocument.text);
    setSearchTerm("");
    setLibraryView("documents");
    setViewMode("editor");
    writeStoredValue(ACTIVE_DOCUMENT_KEY, nextDocument.id);

    void saveDocumentLocally(nextDocument)
      .then(() => {
        setSavePhase(storageModeRef.current === "drive" ? "local" : "saved");
      })
      .catch((error: unknown) => {
        setSavePhase("error");
        setLibraryError(error instanceof Error ? error.message : String(error));
      });

    if (storageModeRef.current === "drive") {
      markDriveDirty(nextDocument.id);
      scheduleDriveSync();
    }

    if (currentSnapshot) {
      const currentChanged = hasDocumentChanges(existingDocument, currentSnapshot);
      void saveDocumentLocally(currentSnapshot).catch((error: unknown) => {
        setSavePhase("error");
        setLibraryError(error instanceof Error ? error.message : String(error));
      });

      if (storageModeRef.current === "drive" && (currentChanged || !currentSnapshot.driveFileId)) {
        markDriveDirty(currentSnapshot.id);
        void syncDriveDirtyDocuments([currentSnapshot.id]);
      }
    }
  }, [
    currentDocumentId,
    documentText,
    documentTitle,
    finishDictation,
    markDriveDirty,
    saveDocumentLocally,
    scheduleDriveSync,
    syncDriveDirtyDocuments,
    updateCurrentDocumentId,
    updateDocumentText,
    updateDocumentTitle
  ]);

  const getCurrentDocumentSnapshot = useCallback(() => {
    const activeDocumentId = currentDocumentIdRef.current;
    const existingDocument = documentsRef.current.find(
      (document) => document.id === activeDocumentId
    );

    return createLocalDocument({
      ...existingDocument,
      id: activeDocumentId || existingDocument?.id,
      title: documentTitleRef.current,
      text: documentTextRef.current,
      createdAt: existingDocument?.createdAt,
      status: existingDocument?.status,
      updatedAt: Date.now()
    });
  }, []);

  const exportDocumentSnapshot = useCallback(async (documentToExport: LocalDocument) => {
    setLibraryError("");

    try {
      await saveMarkdownFile(
        getDocumentExportFileName(documentToExport),
        documentToExport.text.trimEnd().concat("\n")
      );
      setActionMenuDocumentId("");
    } catch (error) {
      setLibraryError(error instanceof Error ? error.message : String(error));
    }
  }, []);

  const exportDocument = useCallback(() => {
    const currentSnapshot = getCurrentDocumentSnapshot();
    void exportDocumentSnapshot(currentSnapshot);
    void persistCurrentDocument({ syncDrive: true }).catch((error: unknown) => {
      setLibraryError(error instanceof Error ? error.message : String(error));
    });
  }, [exportDocumentSnapshot, getCurrentDocumentSnapshot, persistCurrentDocument]);

  const exportAllDocuments = useCallback(() => {
    const currentSnapshot = getCurrentDocumentSnapshot();
    const sourceDocuments = documentsRef.current.length > 0 ? documentsRef.current : documents;
    const exportDocuments = sourceDocuments
      .map((document) => (document.id === currentSnapshot.id ? currentSnapshot : document))
      .filter((document) => getDocumentStatus(document) !== "trashed");

    if (!exportDocuments.length) {
      return;
    }

    void saveMarkdownFile(
      `speechdraft-documents-${formatExportTimestamp()}.md`,
      formatMarkdownBundle(exportDocuments)
    ).catch((error: unknown) => {
      setLibraryError(error instanceof Error ? error.message : String(error));
    });
    void persistCurrentDocument({ syncDrive: true }).catch((error: unknown) => {
      setLibraryError(error instanceof Error ? error.message : String(error));
    });
  }, [documents, getCurrentDocumentSnapshot, persistCurrentDocument]);

  const exportLibraryDocument = useCallback(
    (documentToExport: LocalDocument) => {
      const exportSnapshot =
        documentToExport.id === currentDocumentIdRef.current
          ? getCurrentDocumentSnapshot()
          : documentToExport;

      void exportDocumentSnapshot(exportSnapshot);
    },
    [exportDocumentSnapshot, getCurrentDocumentSnapshot]
  );

  const moveDocumentToStatus = useCallback(
    async (document: LocalDocument, status: LibraryDocumentStatus) => {
      await persistCurrentDocument({ syncDrive: true });

      const now = Date.now();
      const nextDocument = createLocalDocument({
        ...document,
        status,
        archivedAt: undefined,
        trashedAt: status === "trashed" ? now : undefined,
        updatedAt: now
      });
      const nextDocuments = documentsRef.current
        .map((item) => (item.id === document.id ? nextDocument : item))
        .sort((left, right) => right.updatedAt - left.updatedAt);
      const editableDocuments = await ensureEditableCurrentDocument(nextDocuments);
      const savedDocument = await saveDocumentLocally(nextDocument);
      if (storageModeRef.current === "drive") {
        markDriveDirty(savedDocument.id);
        await syncDriveDirtyDocuments([savedDocument.id]);
      }
      const savedDocuments = editableDocuments
        .map((item) => (item.id === savedDocument.id ? savedDocument : item))
        .sort((left, right) => right.updatedAt - left.updatedAt);

      documentsRef.current = savedDocuments;
      setDocuments(savedDocuments);
      setActionMenuDocumentId("");
    },
    [
      ensureEditableCurrentDocument,
      markDriveDirty,
      persistCurrentDocument,
      saveDocumentLocally,
      syncDriveDirtyDocuments
    ]
  );

  const permanentlyDeleteDocument = useCallback(
    async (document: LocalDocument) => {
      if (!window.confirm("Delete this document forever?")) {
        return;
      }

      const nextDocuments = documentsRef.current.filter((item) => item.id !== document.id);
      const editableDocuments = await ensureEditableCurrentDocument(nextDocuments);

      await deleteDocumentFromStorage(document);
      documentsRef.current = editableDocuments;
      setDocuments(editableDocuments);
      setActionMenuDocumentId("");
    },
    [deleteDocumentFromStorage, ensureEditableCurrentDocument]
  );

  const emptyTrash = useCallback(async () => {
    const trashIds = documentsRef.current
      .filter((document) => getDocumentStatus(document) === "trashed")
      .map((document) => document.id);

    if (trashIds.length === 0) {
      return;
    }

    const noun = trashIds.length === 1 ? "document" : "documents";
    if (!window.confirm(`Delete ${trashIds.length} ${noun} forever?`)) {
      return;
    }

    const trashDocuments = documentsRef.current.filter((document) => trashIds.includes(document.id));
    const nextDocuments = documentsRef.current.filter((document) => !trashIds.includes(document.id));
    const editableDocuments = await ensureEditableCurrentDocument(nextDocuments);

    await deleteDocumentsFromStorage(trashDocuments);
    documentsRef.current = editableDocuments;
    setDocuments(editableDocuments);
    setActionMenuDocumentId("");
  }, [deleteDocumentsFromStorage, ensureEditableCurrentDocument]);

  const connectDrive = useCallback(async () => {
    if (!isGoogleDriveConfigured(GOOGLE_CLIENT_ID) || driveStatus === "connecting") {
      return;
    }

    try {
      setLibraryError("");
      setSavePhase("saving");
      setDriveStatus("connecting");

      await requestDriveAccess(GOOGLE_CLIENT_ID);

      if (micActiveRef.current) {
        await finishDictation();
      }

      await persistCurrentDocument();
      const localDocuments = await listDocuments();
      let driveDocuments = await listDriveDocuments();

      if (driveDocuments.length === 0 && localDocuments.length > 0) {
        driveDocuments = await Promise.all(
          localDocuments.map((document) =>
            saveDriveDocument(
              createLocalDocument({
                ...document,
                driveFileId: undefined
              })
            )
          )
        );
        await Promise.all(
          driveDocuments.map((document) =>
            saveDocument(document).catch(() => {
              // Drive is authoritative after connect; local migration cache is best-effort.
            })
          )
        );
      }

      if (driveDocuments.length === 0) {
        const starterDocument = await saveDriveDocument(createStarterDocument());
        driveDocuments = [starterDocument];
        void saveDocument(starterDocument).catch(() => {
          // Drive is authoritative after connect; local cache is best-effort.
        });
      }

      driveDocuments = await Promise.all(
        driveDocuments.map((document) =>
          saveDriveDocument(document).catch(() => document)
        )
      );
      await Promise.all(
        driveDocuments.map((document) =>
          saveDocument(document).catch(() => {
            // Drive is authoritative after connect; local cache is best-effort.
          })
        )
      );

      driveDocuments = driveDocuments.sort((left, right) => right.updatedAt - left.updatedAt);
      driveDirtyIdsRef.current.clear();
      driveDirtySinceRef.current.clear();
      if (driveSyncTimerRef.current !== undefined) {
        window.clearTimeout(driveSyncTimerRef.current);
        driveSyncTimerRef.current = undefined;
      }
      storageModeRef.current = "drive";
      documentsRef.current = driveDocuments;
      documentsReadyRef.current = true;
      setStorageMode("drive");
      setDriveStatus("ready");
      setDocuments(driveDocuments);
      setDocumentsReady(true);

      const activeDocumentId = readStoredValue(ACTIVE_DOCUMENT_KEY, "", LEGACY_ACTIVE_DOCUMENT_KEYS);
      const selectedDocument =
        driveDocuments.find((document) => document.id === activeDocumentId) ??
        driveDocuments.find((document) => getDocumentStatus(document) !== "trashed") ??
        driveDocuments[0];

      if (selectedDocument) {
        applyCurrentDocument(selectedDocument);
      }
      setSavePhase("saved");
    } catch (error) {
      setDriveStatus(storageModeRef.current === "drive" ? "ready" : "available");
      setSavePhase("error");
      setLibraryError(getDriveConnectionErrorMessage(error));
    }
  }, [applyCurrentDocument, driveStatus, finishDictation, persistCurrentDocument]);

  const toggleLibrary = useCallback(() => {
    if (viewMode === "library") {
      void openEditor();
      return;
    }

    openLibrary();
  }, [openEditor, openLibrary, viewMode]);

  const cloudBackupConnected = storageMode === "drive";
  const cloudBackupUnavailable = driveStatus === "unavailable";
  const cloudBackupError =
    !cloudBackupUnavailable && Boolean(libraryError || savePhase === "error");
  const cloudBackupLabel = cloudBackupUnavailable
    ? "Cloud backup unavailable"
    : cloudBackupConnected
      ? "Cloud backup connected"
      : "Connect cloud backup";
  const driveAction = (
    <button
      className={[
        "quiet-action",
        "drive-action",
        cloudBackupConnected ? "connected" : "disconnected",
        cloudBackupUnavailable ? "unavailable" : "",
        cloudBackupError ? "error" : ""
      ]
        .filter(Boolean)
        .join(" ")}
      type="button"
      onClick={() => void connectDrive()}
      onPointerDown={() => {
        void preloadGoogleDriveSignIn(GOOGLE_CLIENT_ID).catch(() => {
          // Drive is optional; connection attempts surface the user-facing error.
        });
      }}
      disabled={cloudBackupUnavailable || driveStatus === "connecting"}
      aria-label={cloudBackupLabel}
      title={cloudBackupLabel}
    >
      {cloudBackupConnected ? <Cloud size={18} /> : <CloudOff size={18} />}
    </button>
  );

  const settingsMenu = (
    <div className="settings-control" onClick={(event) => event.stopPropagation()}>
      <button
        className={settingsOpen ? "quiet-action active" : "quiet-action"}
        type="button"
        onClick={() => setSettingsOpen((isOpen) => !isOpen)}
        aria-label="Settings"
        title="Settings"
      >
        <MoreHorizontal size={18} />
      </button>
      {settingsOpen && (
        <div className="settings-menu" role="dialog" aria-label="Settings">
          <div className="settings-section settings-page-width">
            <div className="settings-section-header">
              <span>Page width</span>
              <span>{pageWidth}px</span>
            </div>
            <input
              aria-label="Page width"
              max={MAX_PAGE_WIDTH}
              min={MIN_PAGE_WIDTH}
              step={20}
              type="range"
              value={pageWidth}
              onChange={(event) => setPageWidth(clampPageWidth(Number(event.target.value)))}
            />
          </div>
          <div className="settings-section settings-text-size">
            <div className="settings-section-header">
              <span>Text size</span>
              <span>{textSize}px</span>
            </div>
            <div className="text-size-control">
              <button
                type="button"
                aria-label="Decrease text size"
                onClick={() => setTextSize((currentSize) => clampTextSize(currentSize - 1))}
              >
                A
              </button>
              <input
                aria-label="Text size"
                max={MAX_TEXT_SIZE}
                min={MIN_TEXT_SIZE}
                step={0.5}
                type="range"
                value={textSize}
                onInput={(event) => setTextSize(clampTextSize(Number(event.currentTarget.value)))}
                onChange={(event) => setTextSize(clampTextSize(Number(event.currentTarget.value)))}
              />
              <button
                className="text-size-larger"
                type="button"
                aria-label="Increase text size"
                onClick={() => setTextSize((currentSize) => clampTextSize(currentSize + 1))}
              >
                A
              </button>
            </div>
          </div>
          <div className="settings-section settings-paragraph-style">
            <div className="settings-section-header">
              <span>New paragraphs</span>
            </div>
            <div className="settings-segmented" role="group" aria-label="New paragraph style">
              <button
                className={paragraphBreakStyle === "indented" ? "active" : ""}
                type="button"
                onClick={() => setParagraphBreakStyle("indented")}
              >
                Indent
              </button>
              <button
                className={paragraphBreakStyle === "spaced" ? "active" : ""}
                type="button"
                onClick={() => setParagraphBreakStyle("spaced")}
              >
                Space
              </button>
            </div>
          </div>
          <button
            className="settings-menu-command"
            type="button"
            onClick={() => {
              setSettingsOpen(false);
              setDictionaryOpen(true);
            }}
          >
            <BookOpenText size={15} />
            Dictionary
          </button>
          {viewMode === "library" && (
            <button
              className="settings-menu-command mobile-library-menu-command"
              type="button"
              onClick={() => {
                setSettingsOpen(false);
                setLibraryView(libraryView === "trash" ? "documents" : "trash");
              }}
            >
              {libraryView === "trash" ? <LayoutGrid size={15} /> : <Trash2 size={15} />}
              {libraryView === "trash" ? "Documents" : "Trash"}
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <main className={appClassName} style={appStyle}>
      <header className="top-chrome">
        <div className="brand">
          <button
            className={viewMode === "library" ? "library-toggle active" : "library-toggle"}
            type="button"
            onClick={toggleLibrary}
            aria-label={viewMode === "library" ? "Return to editor" : "Open documents"}
            title="Documents"
          >
            <LayoutGrid size={19} />
          </button>
          <span>SpeechDraft</span>
        </div>

        <div className="document-title-wrap">
          {viewMode === "library" ? (
            <div className="document-title-row">
              <span className="document-title-label">{getLibraryTitle(libraryView)}</span>
            </div>
          ) : (
            <div className="document-title-row">
              {micActive && <span className="recording-dot" aria-label="Listening" />}
              <input
                aria-label="Document title"
                placeholder={titlePlaceholder}
                size={titleInputSize}
                value={documentTitle}
                onChange={(event) => updateDocumentTitle(event.target.value)}
                onBlur={() => {
                  void persistCurrentDocument({ syncDrive: true });
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    event.currentTarget.blur();
                  }
                }}
              />
            </div>
          )}
        </div>

        <div className="top-actions">
          {viewMode === "library" ? (
            <>
              {libraryView === "trash" ? (
                <button
                  className="quiet-action mobile-library-action danger-action"
                  type="button"
                  onClick={() => void emptyTrash()}
                  disabled={trashedDocuments.length === 0}
                  aria-label="Empty trash"
                  title="Empty trash"
                >
                  <Trash2 size={18} />
                </button>
              ) : (
                <button
                  className="quiet-action mobile-library-action"
                  type="button"
                  onClick={exportAllDocuments}
                  disabled={nonTrashDocuments.length === 0}
                  aria-label="Download all documents"
                  title="Download all"
                >
                  <Download size={18} />
                </button>
              )}
              <button
                className={libraryView === "trash" ? "quiet-action active mobile-trash-action" : "quiet-action mobile-trash-action"}
                type="button"
                onClick={() => setLibraryView(libraryView === "trash" ? "documents" : "trash")}
                aria-label="Open trash folder"
                title="Trash folder"
              >
                <Trash2 size={18} />
              </button>
              <button
                className="quiet-action"
                type="button"
                onClick={() => void createNewDocument()}
                aria-label="New document"
                title="New document"
              >
                <SquarePen size={18} />
              </button>
              {driveAction}
              {settingsMenu}
            </>
          ) : (
            <>
              <button className="quiet-action" type="button" onClick={exportDocument} title="Export">
                <Download size={18} />
              </button>
              <button
                className="quiet-action"
                type="button"
                onClick={() => void createNewDocument()}
                aria-label="New document"
                title="New document"
              >
                <SquarePen size={18} />
              </button>
              {driveAction}
              {settingsMenu}
            </>
          )}
        </div>
      </header>

      {viewMode === "library" ? (
        <section className="library-stage" aria-label="Documents" key="library">
          <div className="library-shell">
            <div className="library-controls">
              <label className="search-field">
                <Search size={17} />
                <input
                  aria-label="Search documents"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </label>
            </div>

            <div className="document-grid">
              {filteredDocuments.map((document) => {
                const status = getDocumentStatus(document);

                return (
                  <div
                    className={
                      document.id === currentDocumentId ? "document-card active" : "document-card"
                    }
                    key={document.id}
                  >
                    <button
                      className="document-card-open"
                      type="button"
                      onClick={() => void openDocument(document)}
                    >
                      <span className="document-card-title">{getDocumentDisplayTitle(document)}</span>
                      <span className="document-card-date">
                        {formatDocumentDate(document.updatedAt)}
                      </span>
                      <span
                        className={
                          getDocumentPreview(document.text)
                            ? "document-card-preview"
                            : "document-card-preview empty"
                        }
                      >
                        {getDocumentPreview(document.text)}
                      </span>
                    </button>
                    <button
                      className="document-menu-trigger"
                      type="button"
                      aria-label={`Actions for ${getDocumentDisplayTitle(document)}`}
                      title="More"
                      onClick={(event) => {
                        event.stopPropagation();
                        setActionMenuDocumentId((currentId) =>
                          currentId === document.id ? "" : document.id
                        );
                      }}
                    >
                      <MoreHorizontal size={18} />
                    </button>
                    {actionMenuDocumentId === document.id && (
                      <div className="document-menu" role="menu" onClick={(event) => event.stopPropagation()}>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => exportLibraryDocument(document)}
                        >
                          <Download size={15} />
                          Download
                        </button>
                        {status !== "active" && (
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => void moveDocumentToStatus(document, "active")}
                          >
                            <RotateCcw size={15} />
                            Restore
                          </button>
                        )}
                        {status !== "trashed" ? (
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => void moveDocumentToStatus(document, "trashed")}
                          >
                            <Trash2 size={15} />
                            Trash
                          </button>
                        ) : (
                          <button
                            className="danger-menu-item"
                            type="button"
                            role="menuitem"
                            onClick={() => void permanentlyDeleteDocument(document)}
                          >
                            <Trash2 size={15} />
                            Delete forever
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {documentsReady && filteredDocuments.length === 0 && (
                <div className="library-empty">No documents</div>
              )}
            </div>
          </div>
        </section>
      ) : (
        <section className="writing-stage" ref={stageRef} key="editor">
          <section className="paper-shell" aria-label="Document editor">
            <textarea
              ref={documentRef}
              aria-label="Document body"
              value={documentText}
              onChange={(event) => {
                setCorrectionPopover(null);
                lastTextInputAtRef.current = Date.now();
                updateDocumentText(event.target.value);
              }}
              onFocus={() => {
                editorFocusedRef.current = true;
                setEditorFocused(true);
                layoutViewportHeightRef.current = Math.max(
                  layoutViewportHeightRef.current,
                  getLayoutViewportHeight()
                );
                queueFocusedEditorCaretScroll();
              }}
              onBlur={() => {
                editorFocusedRef.current = false;
                setEditorFocused(false);
                if (caretScrollAnimationRef.current !== undefined) {
                  window.cancelAnimationFrame(caretScrollAnimationRef.current);
                  caretScrollAnimationRef.current = undefined;
                }
                caretScrollTimerRefs.current.forEach((timerId) => window.clearTimeout(timerId));
                caretScrollTimerRefs.current = [];
                touchScrollRef.current = null;
              }}
              onSelect={(event) => {
                void event;
                handleEditorSelection();
                if (Date.now() - lastTextInputAtRef.current > 180) {
                  queueFocusedEditorCaretScroll();
                }
              }}
              onKeyUp={(event) => {
                void event;
                handleEditorSelection();
              }}
              onMouseUp={handleEditorSelection}
              onWheel={scrollWritingStage}
              onTouchStart={startWritingStageTouchScroll}
              onTouchMove={moveWritingStageTouchScroll}
              onTouchEnd={endWritingStageTouchScroll}
              onTouchCancel={endWritingStageTouchScroll}
              placeholder="Write or dictate..."
              spellCheck
            />
          </section>

          {correctionPopover && (
            <div
              ref={correctionPopoverRef}
              className="correction-popover"
              style={{ left: correctionPopover.left, top: correctionPopover.top }}
              onMouseDown={(event) => event.stopPropagation()}
            >
              {correctionPopover.mode === "offer" ? (
                <button
                  className="correction-command"
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={openCorrectionEditor}
                >
                  <SpellCheck size={15} />
                  Correct spelling
                </button>
              ) : (
                <form className="correction-form" onSubmit={saveSelectionCorrection}>
                  <input
                    ref={correctionInputRef}
                    aria-label="Correction"
                    value={correctionPopover.replacement}
                    onChange={(event) =>
                      setCorrectionPopover((current) =>
                        current ? { ...current, replacement: event.target.value } : current
                      )
                    }
                  />
                  <button type="submit" aria-label="Save correction">
                    <Check size={15} />
                  </button>
                  <button
                    type="button"
                    aria-label="Cancel correction"
                    onClick={() => setCorrectionPopover(null)}
                  >
                    <X size={15} />
                  </button>
                </form>
              )}
            </div>
          )}

          <div className={micActive ? "dictation-control active" : "dictation-control"}>
            {voiceStatusMessage && (
              <div className={voiceUnavailable ? "voice-status error" : "voice-status"} role="status">
                {voiceStatusMessage}
              </div>
            )}
            <button
              className={dialogueActionClass}
              type="button"
              onClick={isDialogueActive ? stopMic : startDialogue}
              disabled={voiceControlsDisabled || (micActive && !isDialogueActive)}
              aria-label={isDialogueActive ? "Finish dialogue dictation" : "Start dialogue dictation"}
              title="Dialogue"
            >
              {isDialogueActive ? (
                <Square size={17} fill="currentColor" />
              ) : (
                <svg
                  aria-hidden="true"
                  className="quote-mark"
                  viewBox="0 0 100 76"
                  focusable="false"
                >
                  <g transform="translate(7 5)">
                    <path d="M3 34c0-16 10-26 27-30l4 10c-9 3-14 8-15 15h16v26H10c-5 0-7-3-7-8V34z" />
                    <path d="M47 34c0-16 10-26 27-30l4 10c-9 3-14 8-15 15h16v26H54c-5 0-7-3-7-8V34z" />
                  </g>
                </svg>
              )}
              {isDialogueActive && countdownSeconds !== null && (
                <span className="voice-countdown">{formatCountdown(countdownSeconds)}</span>
              )}
            </button>
            <button
              className={voiceActionClass}
              type="button"
              onClick={isDictationActive ? stopMic : startDictation}
              disabled={voiceControlsDisabled || (micActive && !isDictationActive)}
              aria-label={isDictationActive ? "Finish dictation" : "Start dictation"}
              title="Dictation"
            >
              {isDictationActive ? <Square size={17} fill="currentColor" /> : <Mic size={24} />}
              {isDictationActive && countdownSeconds !== null && (
                <span className="voice-countdown">{formatCountdown(countdownSeconds)}</span>
              )}
            </button>
          </div>
        </section>
      )}

      <footer className={viewMode === "library" ? "bottom-bar library-bottom-bar" : "bottom-bar"}>
        <div className="footer-status">
          <button
            className="issue-trigger"
            type="button"
            onClick={openIssueReporter}
            aria-label="Report an issue"
            title="Report an issue"
          >
            <MessageSquareWarning size={15} />
          </button>
          <span>
            {viewMode === "library"
              ? getLibraryCountLabel(libraryView, libraryDocuments.length)
              : `${documentStats.words} words`}
          </span>
        </div>
        {viewMode === "editor" ? (
          <div className={micActive ? "voice-strip active" : "voice-strip"} aria-hidden={!micActive}>
            <div className="voice-meter" aria-label="Voice level">
              <span style={{ width: `${level}%` }} />
            </div>
          </div>
        ) : (
          <div />
        )}
        <div className="footer-actions">
          {viewMode === "library" && (
            libraryView === "trash" ? (
              <button
                className="footer-command danger-action"
                type="button"
                onClick={() => void emptyTrash()}
                disabled={trashedDocuments.length === 0}
              >
                <Trash2 size={15} />
                Empty trash
              </button>
            ) : (
              <button
                className="footer-command"
                type="button"
                onClick={exportAllDocuments}
                disabled={nonTrashDocuments.length === 0}
              >
                <Download size={15} />
                Download all
              </button>
            )
          )}
        </div>
      </footer>

      {issueModalOpen && (
        <div className="issue-backdrop" onMouseDown={closeIssueReporter}>
          <section
            className="issue-panel"
            aria-label="Report an issue"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="issue-header">
              <h2>Report an issue</h2>
              <button
                className="issue-icon-button"
                type="button"
                onClick={closeIssueReporter}
                disabled={issueSubmitPhase === "sending"}
                aria-label="Close issue reporter"
              >
                <X size={17} />
              </button>
            </div>
            <form className="issue-form" onSubmit={submitIssueReport}>
              <textarea
                aria-label="Issue"
                maxLength={ISSUE_REPORT_MAX_LENGTH}
                placeholder="What happened?"
                value={issueText}
                onChange={(event) => {
                  setIssueText(event.target.value);
                  if (issueSubmitPhase !== "sending") {
                    setIssueError("");
                    setIssueSubmitPhase("idle");
                  }
                }}
              />
              <div className="issue-form-footer">
                <span
                  className={
                    issueSubmitPhase === "error"
                      ? "issue-status error"
                      : issueSubmitPhase === "sent"
                        ? "issue-status sent"
                        : "issue-status"
                  }
                  role={issueSubmitPhase === "error" ? "alert" : "status"}
                >
                  {issueSubmitPhase === "sent"
                    ? "Sent"
                    : issueSubmitPhase === "error"
                      ? issueError
                      : `${issueText.length}/${ISSUE_REPORT_MAX_LENGTH}`}
                </span>
                <button
                  className="issue-send-button"
                  type="submit"
                  disabled={!issueText.trim() || issueSubmitPhase === "sending"}
                >
                  {issueSubmitPhase === "sent" ? <Check size={15} /> : <Send size={15} />}
                  {issueSubmitPhase === "sending" ? "Sending" : issueSubmitPhase === "sent" ? "Sent" : "Send"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {dictionaryOpen && (
        <div className="dictionary-backdrop" onMouseDown={() => setDictionaryOpen(false)}>
          <section
            className="dictionary-panel"
            aria-label="Dictionary"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="dictionary-header">
              <h2>Dictionary</h2>
              <button
                className="dictionary-icon-button"
                type="button"
                onClick={() => setDictionaryOpen(false)}
                aria-label="Close dictionary"
              >
                <X size={17} />
              </button>
            </div>

            <form className="dictionary-add-form" onSubmit={addDictionaryCorrection}>
              <input
                aria-label="Speech text"
                placeholder="Speech text"
                value={newCorrectionSource}
                onChange={(event) => setNewCorrectionSource(event.target.value)}
              />
              <input
                aria-label="Correction"
                placeholder="Correction"
                value={newCorrectionReplacement}
                onChange={(event) => setNewCorrectionReplacement(event.target.value)}
              />
              <button type="submit" aria-label="Add correction">
                <Plus size={16} />
              </button>
            </form>

            <div className="dictionary-list">
              {corrections.length === 0 ? (
                <div className="dictionary-empty">No corrections</div>
              ) : (
                corrections.map((entry) => (
                  <div className="dictionary-row" key={entry.id}>
                    <input
                      aria-label="Speech text"
                      value={entry.source}
                      onChange={(event) =>
                        updateDictionaryCorrection(entry.id, { source: event.target.value })
                      }
                    />
                    <input
                      aria-label="Correction"
                      value={entry.replacement}
                      onChange={(event) =>
                        updateDictionaryCorrection(entry.id, { replacement: event.target.value })
                      }
                    />
                    <button
                      className="dictionary-icon-button danger-action"
                      type="button"
                      aria-label="Delete correction"
                      onClick={() => deleteDictionaryCorrection(entry.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>
      )}

      {(micError || libraryError) && (
        <div className="voice-error" role="alert">
          {micError || libraryError}
        </div>
      )}

    </main>
  );
}
